/**
 * HR Compensation — Feb 2026.
 *
 * Two-panel layout:
 *   • Left: workspace-wide salary rollup (KPI tiles + by-department table).
 *   • Right: per-employee compensation history (list + "New revision" dialog).
 *
 * Gated to `hr.compensation.manage` — the whole page renders "Locked"
 * for roles that don't hold that perm (or workspace owner/admin bypass).
 * The salary rollup card here + the mirror on FinanceReports both call
 * the same `/workspaces/{ws}/finance/salary-rollup` endpoint.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fmtMoney } from "@/lib/currency";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, TrendingUp, Users2, Building2, Lock, Loader2, X, ChevronRight, Download, Upload, GitCompare, ArrowUp, ArrowDown, BarChart3, PieChart as PieChartIcon, Sparkles, Table as TableIcon, LayoutList } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend, CartesianGrid, Area, AreaChart } from "recharts";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";


// Feb 2026 · CSV export headers — MUST match the bulk-import aliases
// on the backend (`_COMP_ALIASES` in routes_compensation.py) so admins
// can round-trip through Excel.
// r-comp-cadence · Feb 2026 · Extended columns matching backend
// _BULK_EXPORT_HEADERS. Read-only reference cols (emp_code, designation,
// department, reporting_manager, email) come before the numeric fields.
const COMP_CSV_HEADERS = [
  "employee_id", "emp_code", "full_name", "designation",
  "department", "reporting_manager", "email",
  "basic", "hra", "special_allowance",
  "bonus_monthly",              // Monthly Performance Bonus
  "other_allowances",
  "quarterly_fixed", "quarterly_performance_bonus",
  "annual_fixed", "annual_performance_bonus",
  "employer_pf", "employer_esi", "gratuity_annual",
  "employee_pf", "tds_monthly", "professional_tax", "other_deductions",
  "currency", "effective_from", "reason", "notes",
];
function _csvCell(v) {
  const s = v === undefined || v === null ? "" : String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function _dl(filename, content) {
  const blob = new Blob(["\ufeff" + content],
    { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const REASON_OPTIONS = [
  { value: "hiring",           label: "Hiring" },
  { value: "annual_raise",     label: "Annual raise" },
  { value: "promotion",        label: "Promotion" },
  { value: "role_change",      label: "Role change" },
  { value: "market_correction", label: "Market correction" },
  { value: "correction",       label: "Data correction" },
  { value: "adjustment",       label: "Adjustment" },
  { value: "other",            label: "Other" },
];

const EMPTY_COMPONENTS = {
  basic: 0, hra: 0, special_allowance: 0, bonus_monthly: 0,
  other_allowances: 0,
  // r-comp-cadence · Feb 2026
  quarterly_fixed: 0, quarterly_performance_bonus: 0,
  annual_fixed: 0, annual_performance_bonus: 0,
  employer_pf: 0, employer_esi: 0,
  gratuity_annual: 0, employee_pf: 0, tds_monthly: 0,
  professional_tax: 0, other_deductions: 0,
};


export default function HRCompensation() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can, ready, isAdminRole } = useWorkspacePerms();
  const allowed = can("hr.compensation.manage") || isAdminRole;
  const [activeTab, setActiveTab] = useState("employees");
  // Feb 2026 · Cross-tab jump target from the Signals panel so a click on
  // a flagged row lands the user on the Employees tab with that employee
  // pre-selected and the New-revision dialog optionally pre-opened.
  const [jumpTarget, setJumpTarget] = useState(null); // {eid, openDialog, seedComponent}
  const jumpToEmployee = (eid, opts = {}) => {
    setJumpTarget({ eid, ...opts, at: Date.now() });
    setActiveTab("employees");
  };

  if (!ready) {
    return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!allowed) {
    return (
      <div className="p-6">
        <PageHeader title="Compensation" description="Per-employee salary history + workspace-wide rollup." />
        <Card className="mt-4"><CardContent className="p-8 text-center">
          <Lock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <div className="font-semibold">Locked</div>
          <div className="text-sm text-muted-foreground mt-1">
            Ask a workspace admin to grant <code>hr.compensation.manage</code> on <code>/app/roles</code> if you need access.
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="hr-compensation-page">
      <PageHeader
        title="Compensation"
        description="Per-employee salary history + workspace-wide rollup. Every save creates a new revision keyed by `effective_from` — no edits, no data loss."
      />
      <CompToolbar wsId={wsId} />
      <SalaryRollupCard wsId={wsId} />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="sticky top-12 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-background/95 backdrop-blur border-b border-border" data-testid="comp-tabs-sticky">
          <TabsList className="grid w-full grid-cols-3 sm:w-auto sm:inline-flex" data-testid="comp-tabs">
            <TabsTrigger value="employees" data-testid="comp-tab-employees">
              <LayoutList className="w-4 h-4 mr-2" />Employees
            </TabsTrigger>
            <TabsTrigger value="grid" data-testid="comp-tab-grid">
              <TableIcon className="w-4 h-4 mr-2" />Grid
            </TabsTrigger>
            <TabsTrigger value="insights" data-testid="comp-tab-insights">
              <Sparkles className="w-4 h-4 mr-2" />AI Intelligence
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="employees" className="mt-4">
          <EmployeeCompensationPanel wsId={wsId} jumpTarget={jumpTarget} clearJumpTarget={() => setJumpTarget(null)} />
        </TabsContent>
        <TabsContent value="grid" className="mt-4">
          <CompensationGrid wsId={wsId} />
        </TabsContent>
        <TabsContent value="insights" className="mt-4">
          <CompensationInsights wsId={wsId} onJumpToEmployee={jumpToEmployee} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function CompToolbar({ wsId }) {
  const [importing, setImporting] = useState(false);
  const [variance, setVariance] = useState(false);
  const [annualCycle, setAnnualCycle] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Feb 2026 · Round-trip export: both CSV + XLSX come from the backend
  // so the column layout matches the importer exactly. Downloading in
  // browsers requires arraybuffer + Blob so binary XLSX isn't corrupted.
  const doExport = async (fmt /* 'csv' | 'xlsx' */) => {
    setExporting(true);
    try {
      const url = `/workspaces/${wsId}/compensation/bulk-export.${fmt}`;
      const resp = await api.get(url, { responseType: "arraybuffer" });
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv;charset=utf-8";
      const blob = new Blob([resp.data], { type: mime });
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `compensation-${stamp}.${fmt}`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(objUrl);
      toast.success(`Exported compensation (${fmt.toUpperCase()})`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    finally { setExporting(false); }
  };
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => doExport("xlsx")} disabled={exporting} data-testid="comp-export-xlsx-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export XLSX
        </Button>
        <Button size="sm" variant="outline" onClick={() => doExport("csv")} disabled={exporting} data-testid="comp-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => setImporting(true)} data-testid="comp-import-btn">
          <Upload className="w-4 h-4 mr-2" />Bulk import
        </Button>
        <Button size="sm" variant="outline" onClick={() => setVariance(true)} data-testid="comp-variance-btn">
          <GitCompare className="w-4 h-4 mr-2" />Variance
        </Button>
        <Button size="sm" onClick={() => setAnnualCycle(true)} data-testid="comp-annual-cycle-btn">
          <TrendingUp className="w-4 h-4 mr-2" />Annual cycle
        </Button>
      </div>
      {importing && <CompImportDialog wsId={wsId} onClose={() => setImporting(false)} onDone={() => { setImporting(false); window.location.reload(); }} />}
      {variance && <SalaryVarianceDialog wsId={wsId} onClose={() => setVariance(false)} />}
      {annualCycle && <AnnualCycleDialog wsId={wsId} onClose={() => setAnnualCycle(false)} onDone={() => { setAnnualCycle(false); window.location.reload(); }} />}
    </>
  );
}


function CompImportDialog({ wsId, onClose, onDone }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("annual_raise");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = (text) => {
    const out = []; let field = "", record = [], inQ = false, i = 0;
    while (i < text.length) {
      const c = text[i];
      if (inQ) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
        if (c === '"') { inQ = false; i += 1; continue; }
        field += c; i += 1; continue;
      }
      if (c === '"') { inQ = true; i += 1; continue; }
      if (c === ",") { record.push(field); field = ""; i += 1; continue; }
      if (c === "\r") { i += 1; continue; }
      if (c === "\n") { record.push(field); out.push(record); record = []; field = ""; i += 1; continue; }
      field += c; i += 1;
    }
    if (field.length || record.length) { record.push(field); out.push(record); }
    if (out.length === 0) return { headers: [], rows: [] };
    const hdrs = out[0].map((h) => (h || "").trim());
    const dataRows = out.slice(1)
      .filter((r) => r.some((c) => (c || "").trim() !== ""))
      .map((r) => Object.fromEntries(hdrs.map((h, i2) => [h, (r[i2] || "").trim()])));
    return { headers: hdrs, rows: dataRows };
  };

  // Feb 2026 · XLSX support via SheetJS. First worksheet only (matches
  // the bulk-export layout) — README sheet is skipped automatically.
  const parseXlsx = async (file) => {
    const XLSX = await import("xlsx");
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    // Prefer the "Compensation" sheet if present (matches our export
    // template); else fall back to the first sheet.
    const sheetName = wb.SheetNames.find((n) => n.toLowerCase() === "compensation")
      || wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!aoa.length) return { headers: [], rows: [] };
    const hdrs = (aoa[0] || []).map((h) => String(h || "").trim());
    const dataRows = aoa.slice(1)
      .filter((r) => r.some((c) => String(c ?? "").trim() !== ""))
      .map((r) => Object.fromEntries(hdrs.map((h, i2) => [h, r[i2] === undefined || r[i2] === null ? "" : String(r[i2]).trim()])));
    return { headers: hdrs, rows: dataRows };
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const isXlsx = /\.xlsx$/i.test(f.name) || f.type.includes("spreadsheetml");
      const { headers: h, rows: r } = isXlsx
        ? await parseXlsx(f)
        : parseCsv(await f.text());
      setHeaders(h); setRows(r); setResult(null);
      if (r.length === 0) toast.error("No data rows detected");
    } catch { toast.error("Could not read file"); }
    e.target.value = "";
  };

  const submit = async () => {
    if (rows.length === 0) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/compensation/bulk-import`,
        { rows, effective_from: effectiveFrom, reason });
      setResult(data);
      toast.success(`Import complete — ${data.created} added · ${data.skipped} skipped`);
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  const downloadXlsxTemplate = async () => {
    try {
      const resp = await api.get(`/workspaces/${wsId}/compensation/bulk-template.xlsx`,
        { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = "compensation-template.xlsx";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Template download failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="comp-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import compensation</DialogTitle>
          <DialogDescription>
            Upload a CSV or XLSX to migrate an entire salary master. Match by
            <code className="text-[10px] mx-1">employee_id</code>,
            <code className="text-[10px] mx-1">email</code>, or exact
            <code className="text-[10px] mx-1">full_name</code>.
            Recognised component headers (case-insensitive):
            {" "}<code className="text-[10px]">basic, hra, special_allowance, bonus_monthly, other_allowances, quarterly_fixed, quarterly_performance_bonus, annual_fixed, annual_performance_bonus, employer_pf, employer_esi, gratuity_annual, employee_pf, tds_monthly, professional_tax, other_deductions, currency, notes</code>.
            Every row becomes a NEW revision — never overwrites prior history.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={onFilePick} data-testid="comp-import-file-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Effective from *</Label><Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="h-10" data-testid="comp-import-effective-from" /></div>
            <div><Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="comp-import-reason" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="comp-import-choose" className="w-full sm:w-auto">
              <Upload className="w-4 h-4 mr-2" />Choose CSV / XLSX
            </Button>
            <button
              type="button"
              className="text-xs text-primary hover:underline self-center"
              onClick={downloadXlsxTemplate}
              data-testid="comp-import-template-xlsx"
            >
              Download XLSX template
            </button>
            <a
              className="text-xs text-primary hover:underline self-center"
              href={`data:text/csv;charset=utf-8,${encodeURIComponent("employee_id,emp_code,full_name,designation,department,reporting_manager,email,basic,hra,special_allowance,bonus_monthly,other_allowances,quarterly_fixed,quarterly_performance_bonus,annual_fixed,annual_performance_bonus,employer_pf,employer_esi,gratuity_annual,employee_pf,tds_monthly,professional_tax,other_deductions,currency,effective_from,reason,notes\n,,Alice Example,Manager,Engineering,,alice@acme.io,40000,20000,5000,0,0,0,0,0,0,4800,0,20000,4800,3000,200,0,INR,,,\n")}`}
              download="compensation-template.csv"
              data-testid="comp-import-template"
            >
              Download CSV template
            </a>
          </div>
          {rows.length > 0 && !result && (
            <div className="border border-border p-3 text-xs bg-muted/30" data-testid="comp-import-preview">
              <div className="font-semibold mb-1">Preview · {rows.length} row{rows.length === 1 ? "" : "s"} ready</div>
              <div className="text-muted-foreground text-[11px] mb-2">Headers: {headers.join(", ")}</div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {rows.slice(0, 8).map((r, i) => (
                  <li key={i} className="truncate">
                    <strong>{r.full_name || r.email || r.employee_id || "(missing identifier)"}</strong> · basic: {r.basic || 0}
                  </li>
                ))}
                {rows.length > 8 && <li className="italic text-muted-foreground">…and {rows.length - 8} more</li>}
              </ul>
            </div>
          )}
          {result && (
            <div className="border border-border p-3 text-xs bg-emerald-50 dark:bg-emerald-950/20 space-y-1" data-testid="comp-import-result">
              <div><strong>Created:</strong> {result.created}</div>
              <div><strong>Skipped:</strong> {result.skipped}</div>
              {result.skipped > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Show skipped rows</summary>
                  <ul className="mt-1 space-y-0.5 text-[11px] max-h-40 overflow-y-auto">
                    {(result.results || []).filter((r) => r.status === "skipped").map((r, i) => (
                      <li key={i}>Row {r.row}: {r.reason}{r.email ? ` (${r.email})` : ""}{r.name ? ` — ${r.name}` : ""}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => (result ? onDone() : onClose())} className="w-full sm:w-auto">
            {result ? "Done" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={rows.length === 0 || busy || !effectiveFrom} className="w-full sm:w-auto" data-testid="comp-import-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</> : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SalaryVarianceDialog({ wsId, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10);
  const [prev, setPrev] = useState(monthAgo);
  const [curr, setCurr] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/finance/salary-variance`, { params: { prev, curr } });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Variance failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="comp-variance-dialog">
        <DialogHeader>
          <DialogTitle>Salary cost variance</DialogTitle>
          <DialogDescription>Compare payroll cost between two dates and see which revisions drove the delta.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Previous date</Label><Input type="date" value={prev} onChange={(e) => setPrev(e.target.value)} className="h-10" data-testid="variance-prev" /></div>
            <div><Label>Current date</Label><Input type="date" value={curr} onChange={(e) => setCurr(e.target.value)} className="h-10" data-testid="variance-curr" /></div>
            <div className="flex items-end"><Button onClick={load} disabled={loading || prev >= curr} className="w-full h-10" data-testid="variance-refresh">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Compare"}</Button></div>
          </div>
          {data && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <VarianceTile label="Monthly employer cost" prev={data.totals.employer_cost.prev} curr={data.totals.employer_cost.curr} delta={data.totals.employer_cost.delta} pct={data.totals.employer_cost.pct} testid="variance-cost" />
                <VarianceTile label="Net take-home" prev={data.totals.net_take_home.prev} curr={data.totals.net_take_home.curr} delta={data.totals.net_take_home.delta} pct={data.totals.net_take_home.pct} testid="variance-net" />
                <VarianceTile label="Headcount covered" prev={data.totals.headcount.prev} curr={data.totals.headcount.curr} delta={data.totals.headcount.delta} pct={null} isCount testid="variance-hc" />
              </div>
              {data.by_department.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">By department (sorted by |Δ|)</div>
                  <div className="border border-border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40 uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left px-3 py-2">Department</th>
                          <th className="text-right px-3 py-2">Prev</th>
                          <th className="text-right px-3 py-2">Curr</th>
                          <th className="text-right px-3 py-2">Δ</th>
                          <th className="text-right px-3 py-2">%</th>
                          <th className="text-right px-3 py-2">HC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {data.by_department.map((d) => (
                          <tr key={d.department_id} data-testid={`variance-dept-${d.department_id}`}>
                            <td className="px-3 py-2">{d.department_name}</td>
                            <td className="text-right px-3 py-2 font-mono">{fmtMoney(d.prev_employer_cost)}</td>
                            <td className="text-right px-3 py-2 font-mono">{fmtMoney(d.curr_employer_cost)}</td>
                            <td className={`text-right px-3 py-2 font-mono font-semibold ${d.delta > 0 ? "text-emerald-600" : d.delta < 0 ? "text-red-600" : ""}`}>
                              {d.delta > 0 ? "+" : ""}{fmtMoney(d.delta)}
                            </td>
                            <td className={`text-right px-3 py-2 font-mono ${d.pct > 0 ? "text-emerald-600" : d.pct < 0 ? "text-red-600" : ""}`}>
                              {d.pct === null ? "—" : `${d.pct > 0 ? "+" : ""}${d.pct}%`}
                            </td>
                            <td className="text-right px-3 py-2">{d.prev_headcount} → {d.curr_headcount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {data.revisions_in_window.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                    Revisions in window ({data.revisions_in_window.length}) — the "why"
                  </div>
                  <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                    {data.revisions_in_window.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`variance-rev-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{r.employee_name || r.employee_id}</div>
                          <div className="text-muted-foreground text-[10px]">{r.effective_from} · {r.reason}</div>
                        </div>
                        <div className="font-mono">{fmtMoney(r.totals?.employer_cost_monthly || 0)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.revisions_in_window.length === 0 && (
                <div className="text-xs text-muted-foreground italic border border-dashed border-border p-3 text-center">
                  No revisions between {data.prev_as_of} and {data.curr_as_of}.
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VarianceTile({ label, prev, curr, delta, pct, isCount, testid }) {
  const fmt = isCount ? (v) => String(v) : fmtMoney;
  const up = delta > 0;
  const dn = delta < 0;
  return (
    <Card><CardContent className="p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-lg font-bold font-mono">{fmt(curr)}</div>
        <div className={`text-xs font-mono flex items-center gap-0.5 ${up ? "text-emerald-600" : dn ? "text-red-600" : "text-muted-foreground"}`}>
          {up ? <ArrowUp className="w-3 h-3" /> : dn ? <ArrowDown className="w-3 h-3" /> : null}
          {up ? "+" : ""}{fmt(delta)}{pct !== null && pct !== undefined ? ` (${pct}%)` : ""}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">was {fmt(prev)}</div>
    </CardContent></Card>
  );
}


// Feb 2026 · Annual cycle wizard.
// Step 1: preview → POST /compensation/annual-cycle-preview
// Step 2: commit → POST /compensation/bulk-import (one row per selected preview)
function AnnualCycleDialog({ wsId, onClose, onDone }) {
  const nextApril = (() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    return `${y}-04-01`;
  })();
  const [effectiveFrom, setEffectiveFrom] = useState(nextApril);
  const [flatHike, setFlatHike] = useState("");
  const [departments, setDepartments] = useState([]);
  const [scopeDepts, setScopeDepts] = useState([]);   // array of department ids
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(() => new Set());   // employee_ids to commit

  useEffect(() => {
    api.get(`/workspaces/${wsId}/departments`)
      .then((r) => setDepartments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDepartments([]));
  }, [wsId]);

  const runPreview = async () => {
    if (!effectiveFrom) { toast.error("Pick an effective date"); return; }
    setPreviewing(true);
    try {
      const body = {
        effective_from: effectiveFrom,
        flat_hike_pct: flatHike === "" ? null : Number(flatHike),
        scope_department_ids: scopeDepts.length ? scopeDepts : null,
      };
      const { data } = await api.post(
        `/workspaces/${wsId}/compensation/annual-cycle-preview`, body);
      setPreview(data);
      // Auto-select every ready row.
      const ready = new Set(
        (data.previews || [])
          .filter((p) => p.status === "ready")
          .map((p) => p.employee_id));
      setSelected(ready);
      toast.success(`Previewed ${ready.size} employee${ready.size === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setPreviewing(false); }
  };

  const toggleSel = (eid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(eid)) n.delete(eid); else n.add(eid);
      return n;
    });
  };

  const applyRaises = async () => {
    if (!preview || selected.size === 0) return;
    if (!window.confirm(
      `Commit raises for ${selected.size} employee${selected.size === 1 ? "" : "s"} ` +
      `effective ${effectiveFrom}? This creates a new revision per employee — ` +
      `history is preserved, but new revisions cannot be edited (only voided).`)) return;
    setApplying(true);
    try {
      const rows = (preview.previews || [])
        .filter((p) => p.status === "ready" && selected.has(p.employee_id))
        .map((p) => ({
          employee_id: p.employee_id,
          full_name: p.full_name,
          email: p.email,
          ...p.components,
        }));
      const { data } = await api.post(
        `/workspaces/${wsId}/compensation/bulk-import`,
        { effective_from: effectiveFrom, reason: "annual_cycle", rows });
      toast.success(`Applied ${data.created} raise${data.created === 1 ? "" : "s"}` +
        (data.skipped ? ` · ${data.skipped} skipped` : ""));
      onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setApplying(false); }
  };

  const readyCount = preview ? (preview.previews || []).filter((p) => p.status === "ready").length : 0;
  const skipCount = preview ? (preview.skipped || 0) : 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="annual-cycle-dialog">
        <DialogHeader>
          <DialogTitle>Annual compensation cycle</DialogTitle>
          <DialogDescription>
            Plan next year's raises, preview the impact, then commit — every accepted row becomes
            a new revision keyed by the effective date. Existing history is preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Effective from</Label>
              <Input type="date" value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                className="h-10" data-testid="annual-cycle-effective-from" />
            </div>
            <div>
              <Label>Flat hike (%)</Label>
              <Input type="number" inputMode="decimal" step="0.5" placeholder="e.g. 8"
                value={flatHike} onChange={(e) => setFlatHike(e.target.value)}
                className="h-10" data-testid="annual-cycle-flat-hike" />
            </div>
            <div className="flex items-end">
              <Button onClick={runPreview} disabled={previewing || !effectiveFrom}
                className="w-full h-10" data-testid="annual-cycle-preview-btn">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview impact"}
              </Button>
            </div>
          </div>

          {/* Department scope */}
          {departments.length > 0 && (
            <div>
              <Label className="mb-1 block">Scope to departments (optional — leave empty for all)</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {departments.map((d) => {
                  const on = scopeDepts.includes(d.id);
                  return (
                    <button key={d.id} type="button"
                      onClick={() => setScopeDepts((s) => on ? s.filter((x) => x !== d.id) : [...s, d.id])}
                      className={`text-xs px-2 py-1 rounded border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/50"}`}
                      data-testid={`annual-cycle-dept-${d.id}`}
                    >{d.name}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Preview results */}
          {preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryTile label="Roster" value={String(preview.roster_size)} testid="annual-cycle-roster" />
                <SummaryTile label="Ready" value={String(readyCount)} testid="annual-cycle-ready" />
                <SummaryTile label="Δ monthly" value={fmtMoney(preview.total_monthly_delta)} tone="emerald" testid="annual-cycle-delta-monthly" />
                <SummaryTile label="Δ annual" value={fmtMoney(preview.total_annual_delta)} tone="emerald" testid="annual-cycle-delta-annual" />
              </div>

              {skipCount > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 px-3 py-2 rounded">
                  {skipCount} employee{skipCount === 1 ? " has" : "s have"} no current revision — add a starter revision first, then re-run preview.
                </div>
              )}

              <div className="border border-border">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 text-xs">
                  <div className="font-semibold uppercase tracking-widest text-muted-foreground">
                    Per-employee preview ({(preview.previews || []).length})
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-xs text-primary hover:underline"
                      onClick={() => setSelected(new Set((preview.previews || []).filter((p) => p.status === "ready").map((p) => p.employee_id)))}
                      data-testid="annual-cycle-select-all">
                      Select all ready
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" className="text-xs text-primary hover:underline"
                      onClick={() => setSelected(new Set())}
                      data-testid="annual-cycle-clear">
                      Clear
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-mono">{selected.size} selected</span>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/20 uppercase text-muted-foreground sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 w-8"></th>
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-right px-3 py-2">Old gross</th>
                        <th className="text-right px-3 py-2">New gross</th>
                        <th className="text-right px-3 py-2">Δ / mo</th>
                        <th className="text-right px-3 py-2">Hike %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(preview.previews || []).map((p) => {
                        const ready = p.status === "ready";
                        return (
                          <tr key={p.employee_id}
                            className={ready ? "" : "bg-muted/20 text-muted-foreground"}
                            data-testid={`annual-cycle-emp-row-${p.employee_id}`}>
                            <td className="px-3 py-2">
                              {ready ? (
                                <input type="checkbox" checked={selected.has(p.employee_id)}
                                  onChange={() => toggleSel(p.employee_id)}
                                  data-testid={`annual-cycle-sel-${p.employee_id}`} />
                              ) : null}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-semibold truncate max-w-[280px]">{p.full_name || p.email}</div>
                              {!ready && <div className="text-[10px] italic">{p.status.replace(/_/g, " ")}</div>}
                            </td>
                            <td className="text-right px-3 py-2 font-mono">{ready ? fmtMoney(p.old_gross_monthly) : "—"}</td>
                            <td className="text-right px-3 py-2 font-mono">{ready ? fmtMoney(p.new_gross_monthly) : "—"}</td>
                            <td className={`text-right px-3 py-2 font-mono font-semibold ${p.delta_monthly > 0 ? "text-emerald-600" : p.delta_monthly < 0 ? "text-red-600" : ""}`}>
                              {ready ? `${p.delta_monthly > 0 ? "+" : ""}${fmtMoney(p.delta_monthly)}` : "—"}
                            </td>
                            <td className={`text-right px-3 py-2 font-mono ${p.hike_pct_effective > 0 ? "text-emerald-600" : p.hike_pct_effective < 0 ? "text-red-600" : ""}`}>
                              {ready && p.hike_pct_effective !== null ? `${p.hike_pct_effective > 0 ? "+" : ""}${p.hike_pct_effective}%` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">Close</Button>
          <Button onClick={applyRaises}
            disabled={!preview || selected.size === 0 || applying}
            className="w-full sm:w-auto"
            data-testid="annual-cycle-apply-btn">
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
            Apply {selected.size ? `to ${selected.size}` : "raises"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SummaryTile({ label, value, tone, testid }) {
  const cls = tone === "emerald" ? "text-emerald-600" : "";
  return (
    <Card><CardContent className="p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono mt-1 ${cls}`}>{value}</div>
    </CardContent></Card>
  );
}


function SalaryRollupCard({ wsId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/finance/salary-rollup`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load rollup")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  if (loading || !data) {
    return <Card><CardContent className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading rollup…</CardContent></Card>;
  }

  return (
    <Card data-testid="salary-rollup-card">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Live salary rollup</div>
            <div className="text-lg font-bold">As of {data.as_of}</div>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="rollup-refresh"><TrendingUp className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiTile label="Employer cost (monthly)" value={fmtMoney(data.total_employer_cost_monthly)} testid="rollup-employer-cost" />
          <KpiTile label="Net take-home (monthly)" value={fmtMoney(data.total_net_monthly)} testid="rollup-net" />
          <KpiTile label="Employees covered" value={`${data.headcount_covered}/${data.headcount_active}`} sub={data.headcount_missing_revision > 0 ? `${data.headcount_missing_revision} missing revision` : "All active covered"} testid="rollup-headcount" />
          <KpiTile label="Annualised employer cost" value={fmtMoney(data.total_employer_cost_monthly * 12)} testid="rollup-annualised" />
        </div>
        {data.by_department.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><Building2 className="w-3 h-3" />By department</div>
            <div className="border border-border divide-y divide-border">
              {data.by_department.slice(0, 8).map((d) => (
                <div key={d.department_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`rollup-dept-${d.department_id}`}>
                  <div className="min-w-0 flex-1 truncate">{d.department_name}</div>
                  <div className="text-xs text-muted-foreground mr-3">{d.headcount} emp</div>
                  <div className="font-mono font-semibold">{fmtMoney(d.employer_cost_monthly)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({ label, value, sub, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold mt-1 font-mono">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}


function EmployeeCompensationPanel({ wsId, jumpTarget, clearJumpTarget }) {
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [deptId, setDeptId] = useState("all");  // Feb 2026 · dept filter
  const [desig, setDesig] = useState("all");    // Feb 2026 · designation filter
  const [selEmpId, setSelEmpId] = useState(null);
  const [detail, setDetail] = useState(null); // {employee, revisions, current}
  const [loading, setLoading] = useState(false);
  const [newRevOpen, setNewRevOpen] = useState(false);
  const [historyView, setHistoryView] = useState("timeline"); // Feb 2026 · "timeline" | "matrix"

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/employees?status=active`).then((r) => {
      const rows = (r.data?.employees || r.data || [])
        .filter((e) => ["active", "onboarding", "probation", "notice"].includes(e.status));
      setEmployees(rows);
      if (rows.length && !selEmpId) setSelEmpId(rows[0].id);
    });
  }, [wsId]); // eslint-disable-line

  // Feb 2026 · React to jump-target requests coming from the AI Intelligence
  // Signals panel — select the employee, clear filters so they're visible in
  // the sidebar list, and optionally auto-open the New-revision dialog.
  useEffect(() => {
    if (!jumpTarget || !jumpTarget.eid) return;
    setSelEmpId(jumpTarget.eid);
    setDeptId("all"); setDesig("all"); setQ("");
    if (jumpTarget.openDialog) {
      // Wait for loadDetail effect to hydrate before opening the dialog so
      // the seed picks up the current revision.
      setTimeout(() => setNewRevOpen(true), 250);
    }
    // Consume the target so re-entering the tab doesn't re-fire.
    clearJumpTarget && clearJumpTarget();
  }, [jumpTarget]); // eslint-disable-line

  const loadDetail = async (eid) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/employees/${eid}/compensation`);
      setDetail(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load compensation")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (wsId && selEmpId) loadDetail(selEmpId); }, [wsId, selEmpId]); // eslint-disable-line

  // Distinct departments across the loaded roster, sorted by name.
  const departments = useMemo(() => {
    const seen = new Map();
    for (const e of employees) {
      const id = e.department_id || "__unassigned";
      const name = e.department_name || (id === "__unassigned" ? "Unassigned" : id);
      if (!seen.has(id)) seen.set(id, name);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees]);

  // Distinct designations. Filtered by department when set so the two filters
  // narrow together and never surface impossible combinations.
  const designations = useMemo(() => {
    const seen = new Set();
    for (const e of employees) {
      if (deptId !== "all" && (e.department_id || "__unassigned") !== deptId) continue;
      seen.add((e.designation || "").trim() || "—");
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [employees, deptId]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return employees.filter((e) => {
      if (deptId !== "all") {
        const emp_dept = e.department_id || "__unassigned";
        if (emp_dept !== deptId) return false;
      }
      if (desig !== "all") {
        const d = (e.designation || "").trim() || "—";
        if (d !== desig) return false;
      }
      if (!t) return true;
      return (e.full_name || "").toLowerCase().includes(t)
          || (e.designation || "").toLowerCase().includes(t)
          || (e.department_name || "").toLowerCase().includes(t);
    });
  }, [q, deptId, desig, employees]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <Card><CardContent className="p-0">
        <div className="p-3 border-b border-border space-y-2">
          <Input placeholder="Search name / designation / dept…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="comp-emp-search" className="h-9" />
          <Select value={deptId} onValueChange={(v) => { setDeptId(v); setDesig("all"); }}>
            <SelectTrigger className="h-9 text-xs" data-testid="comp-emp-dept-filter">
              <SelectValue placeholder="Filter by department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments ({employees.length})</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} ({employees.filter((e) => (e.department_id || "__unassigned") === d.id).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={desig} onValueChange={setDesig}>
            <SelectTrigger className="h-9 text-xs" data-testid="comp-emp-desig-filter">
              <SelectValue placeholder="Filter by designation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All designations</SelectItem>
              {designations.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-muted-foreground">
            Showing <strong>{filtered.length}</strong> of {employees.length}
          </div>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {filtered.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No employees match</div>}
          {filtered.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setSelEmpId(e.id)}
              className={`w-full text-left px-3 py-2 border-b border-border/50 text-sm ${selEmpId === e.id ? "bg-primary/10 border-l-4 border-l-primary" : "hover:bg-muted"}`}
              data-testid={`comp-emp-${e.id}`}
            >
              <div className="font-medium truncate">{e.full_name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{e.designation || e.employment_type || "—"}{e.department_name ? ` · ${e.department_name}` : ""}</div>
            </button>
          ))}
        </div>
      </CardContent></Card>

      <div className="space-y-3 min-w-0">
        {loading || !detail ? (
          <Card><CardContent className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading…</CardContent></Card>
        ) : (
          <>
            <Card><CardContent className="p-4 sm:p-6 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Employee</div>
                  <div className="text-xl font-bold" data-testid="comp-selected-name">{detail.employee?.full_name}</div>
                </div>
                <Button size="sm" onClick={() => setNewRevOpen(true)} data-testid="comp-new-revision"><Plus className="w-4 h-4 mr-1" />New revision</Button>
              </div>
              {detail.current ? (
                <div className="border border-primary/40 bg-primary/5 p-3">
                  <div className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1">Current · effective {detail.current.effective_from}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-[10px] text-muted-foreground uppercase">Gross monthly</div><div className="font-mono font-bold">{fmtMoney(detail.current.totals?.gross_monthly)}</div></div>
                    <div><div className="text-[10px] text-muted-foreground uppercase">Net take-home</div><div className="font-mono font-bold">{fmtMoney(detail.current.totals?.net_monthly)}</div></div>
                    <div><div className="text-[10px] text-muted-foreground uppercase">Employer cost/m</div><div className="font-mono font-bold">{fmtMoney(detail.current.totals?.employer_cost_monthly)}</div></div>
                    <div><div className="text-[10px] text-muted-foreground uppercase">Annual CTC</div><div className="font-mono font-bold">{fmtMoney(detail.current.totals?.annual_ctc)}</div></div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-border p-4 text-sm text-muted-foreground text-center">
                  No compensation on file. <button type="button" onClick={() => setNewRevOpen(true)} className="text-primary underline">Add first revision</button>
                </div>
              )}
            </CardContent></Card>

            <Card><CardContent className="p-0">
              <div className="p-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Revision history · {detail.revisions.length}</div>
                {detail.revisions.length >= 2 && (
                  <div className="inline-flex border border-border" data-testid="comp-history-view-toggle">
                    <button type="button"
                      className={`text-[11px] px-2.5 py-1 uppercase tracking-widest ${(historyView === "timeline" || !historyView) ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setHistoryView("timeline")}
                      data-testid="comp-history-view-timeline">Timeline</button>
                    <button type="button"
                      className={`text-[11px] px-2.5 py-1 uppercase tracking-widest border-l border-border ${historyView === "matrix" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                      onClick={() => setHistoryView("matrix")}
                      data-testid="comp-history-view-matrix">Compare · %Δ</button>
                  </div>
                )}
              </div>
              {detail.revisions.length === 0 ? (
                <div className="p-6 text-xs text-muted-foreground text-center">No revisions yet.</div>
              ) : historyView === "matrix" && detail.revisions.length >= 2 ? (
                <RevisionMatrix revisions={detail.revisions} currentId={detail.current?.id} />
              ) : (
                <div className="divide-y divide-border">
                  {detail.revisions.map((r) => (
                    <RevisionRow
                      key={r.id}
                      rev={r}
                      isCurrent={detail.current && detail.current.id === r.id}
                      onVoid={async () => {
                        if (!window.confirm(`Void revision effective ${r.effective_from}? History stays visible in the audit trail but this row drops from rollups.`)) return;
                        try {
                          await api.post(`/compensation-revisions/${r.id}/void`);
                          toast.success("Revision voided");
                          loadDetail(selEmpId);
                        } catch (e) { toast.error(apiErrorMessage(e, "Void failed")); }
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent></Card>
          </>
        )}
      </div>

      {newRevOpen && (
        <NewRevisionDialog
          wsId={wsId}
          employeeId={selEmpId}
          seed={detail?.current?.components || EMPTY_COMPONENTS}
          onClose={() => setNewRevOpen(false)}
          onSaved={() => { setNewRevOpen(false); loadDetail(selEmpId); }}
        />
      )}
    </div>
  );
}


// Feb 2026 · Side-by-side comparison of every revision, one column per
// revision (oldest → newest) and one row per component. Each numeric cell
// after the first revision shows two delta chips: `Δprev` (vs the immediately
// preceding revision) and `Δfirst` (vs the very first — "since joined").
function RevisionMatrix({ revisions, currentId }) {
  // Sort ascending: earliest (join) on the left → most recent on the right.
  const ordered = useMemo(() => {
    return [...revisions].sort((a, b) =>
      (a.effective_from || "").localeCompare(b.effective_from || ""));
  }, [revisions]);

  const first = ordered[0];
  const componentRows = [
    { k: "basic", label: "Basic", strong: false },
    { k: "hra", label: "HRA", strong: false },
    { k: "special_allowance", label: "Special allow.", strong: false },
    { k: "bonus_monthly", label: "Monthly Perf. Bonus", strong: false },
    { k: "other_allowances", label: "Other allow.", strong: false },
    // r-comp-cadence · Feb 2026
    { k: "quarterly_fixed", label: "Quarterly Fixed", strong: false },
    { k: "quarterly_performance_bonus", label: "Quarterly Perf.", strong: false },
    { k: "annual_fixed", label: "Annual Fixed", strong: false },
    { k: "annual_performance_bonus", label: "Annual Perf.", strong: false },
    { k: "employer_pf", label: "Employer PF", strong: false },
    { k: "employer_esi", label: "Employer ESI", strong: false },
    { k: "gratuity_annual", label: "Gratuity / yr", strong: false },
    { k: "employee_pf", label: "Employee PF", strong: false },
    { k: "tds_monthly", label: "TDS", strong: false },
    { k: "professional_tax", label: "Prof. Tax", strong: false },
    { k: "other_deductions", label: "Other ded.", strong: false },
  ];
  const totalsRows = [
    { k: "gross_monthly", label: "Gross monthly" },
    { k: "net_monthly", label: "Net take-home" },
    { k: "employer_cost_monthly", label: "Employer cost / mo" },
    { k: "annual_ctc", label: "Annual CTC" },
  ];

  const readVal = (rev, key) => {
    if (rev.components && key in rev.components) return Number(rev.components[key] || 0);
    if (rev.totals && key in rev.totals) return Number(rev.totals[key] || 0);
    return 0;
  };

  // Show at most last 12 revisions to keep the matrix readable; older ones
  // remain accessible via the Timeline view. Order kept ascending so the
  // "since joined" reference (leftmost) always maps to the true first rev.
  const visible = ordered.length > 12
    ? [ordered[0], ...ordered.slice(-11)]   // always include the "join" rev
    : ordered;
  const truncated = ordered.length > 12;

  return (
    <div className="overflow-x-auto" data-testid="comp-revision-matrix">
      <table className="text-xs min-w-full">
        <thead className="bg-muted/40">
          <tr className="border-b border-border">
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground sticky left-0 bg-muted/40 z-10 min-w-[140px]">
              Component
            </th>
            {visible.map((r, idx) => {
              const isFirst = idx === 0;
              const isCurrent = r.id === currentId;
              return (
                <th key={r.id}
                    className={`text-right px-3 py-2 text-[10px] uppercase tracking-widest whitespace-nowrap ${isCurrent ? "text-primary" : "text-muted-foreground"} ${isFirst ? "border-l-2 border-l-primary/30" : ""}`}
                    data-testid={`comp-matrix-col-${r.id}`}>
                  <div className="font-semibold normal-case tracking-normal">
                    {r.effective_from}
                  </div>
                  <div className="text-[9px] font-normal tracking-normal">
                    {isFirst && <span className="text-primary">Joined</span>}
                    {isFirst && isCurrent ? " · " : ""}
                    {isCurrent && <span className="text-primary">Current</span>}
                    {!isFirst && !isCurrent && (r.reason || "").replace(/_/g, " ")}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {componentRows.map((c) => (
            <MatrixRow key={c.k} col={c} visible={visible} first={first} readVal={readVal} />
          ))}
          <tr><td className="p-1" colSpan={visible.length + 1}></td></tr>
          {totalsRows.map((c) => (
            <MatrixRow key={c.k} col={{ ...c, strong: true }} visible={visible} first={first} readVal={readVal} />
          ))}
        </tbody>
      </table>
      {truncated && (
        <div className="text-[10px] text-muted-foreground italic p-2 text-center border-t border-border">
          Showing the join revision + last 11 revisions. Switch to Timeline view to see all {revisions.length}.
        </div>
      )}
    </div>
  );
}

function MatrixRow({ col, visible, first, readVal }) {
  const firstVal = readVal(first, col.k);
  return (
    <tr className={col.strong ? "bg-primary/5 font-semibold" : ""} data-testid={`comp-matrix-row-${col.k}`}>
      <td className={`px-3 py-2 sticky left-0 z-10 whitespace-nowrap ${col.strong ? "bg-primary/5 text-primary" : "bg-background text-foreground"}`}>
        {col.label}
      </td>
      {visible.map((r, idx) => {
        const val = readVal(r, col.k);
        const zero = val === 0 && firstVal === 0;
        const prev = idx > 0 ? readVal(visible[idx - 1], col.k) : 0;
        const dPrev = idx > 0 && prev !== 0 ? ((val - prev) / prev) * 100 : null;
        const dFirst = idx > 0 && firstVal !== 0 ? ((val - firstVal) / firstVal) * 100 : null;
        return (
          <td key={r.id}
              className={`text-right px-3 py-2 font-mono whitespace-nowrap ${idx === 0 ? "border-l-2 border-l-primary/30" : ""} ${zero ? "text-muted-foreground/60" : ""}`}
              data-testid={`comp-matrix-cell-${col.k}-${r.id}`}>
            <div className={col.strong ? "text-primary" : ""}>{fmtMoney(val)}</div>
            {idx > 0 && (
              <div className="flex items-center justify-end gap-1 mt-0.5">
                {dPrev !== null && <DeltaChip pct={dPrev} label="prev" testid={`comp-matrix-dprev-${col.k}-${r.id}`} />}
                {dFirst !== null && <DeltaChip pct={dFirst} label="join" bold testid={`comp-matrix-dfirst-${col.k}-${r.id}`} />}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function DeltaChip({ pct, label, bold, testid }) {
  if (pct === null || pct === undefined || !isFinite(pct)) return null;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) {
    return (
      <span className="text-[9px] font-mono text-muted-foreground" data-testid={testid}>
        <span className="text-[8px] uppercase tracking-wider mr-0.5">{label}</span>—
      </span>
    );
  }
  const up = rounded > 0;
  const tone = up ? "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30" : "text-red-700 bg-red-50 dark:bg-red-950/30";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-mono px-1 rounded-sm ${tone} ${bold ? "font-bold" : ""}`}
          data-testid={testid}>
      <span className="text-[8px] uppercase tracking-wider">{label}</span>
      {up ? "+" : ""}{rounded}%
    </span>
  );
}



function RevisionRow({ rev, isCurrent, onVoid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-sm" data-testid={`comp-revision-${rev.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-left"
      >
        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{rev.effective_from}</span>
            <Badge variant="outline" className="text-[10px]">{(REASON_OPTIONS.find((o) => o.value === rev.reason)?.label) || rev.reason}</Badge>
            {isCurrent && <Badge className="text-[10px]">Current</Badge>}
          </div>
          {rev.notes && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{rev.notes}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono font-semibold">{fmtMoney(rev.totals?.employer_cost_monthly)}</div>
          <div className="text-[10px] text-muted-foreground">employer / month</div>
        </div>
      </button>
      {open && (
        <div className="px-9 pb-3 space-y-2 bg-muted/30">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {Object.entries(rev.components || {}).map(([k, v]) => (
              <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}: </span><span className="font-mono">{fmtMoney(v)}</span></div>
            ))}
          </div>
          <div className="flex justify-end pt-1 gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/compensation-revisions/${rev.id}/offer-letter?auth=${encodeURIComponent(typeof window !== "undefined" ? (localStorage.getItem("co_token") || "") : "")}`}
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`comp-revision-letter-${rev.id}`}
            >
              <Download className="w-3 h-3" />Compensation letter (PDF)
            </a>
            <button
              type="button"
              onClick={async () => {
                const cover = window.prompt("Optional cover note (leave blank to skip):", "");
                if (cover === null) return;
                try {
                  const { data } = await api.post(
                    `/compensation-revisions/${rev.id}/email-letter`,
                    { message: cover || undefined });
                  toast.success(`Letter emailed to ${data.recipient}`);
                } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
              }}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`comp-revision-email-${rev.id}`}
            >
              <Upload className="w-3 h-3" />Email to employee
            </button>
            <button type="button" onClick={onVoid} className="text-xs text-destructive hover:underline inline-flex items-center gap-1" data-testid={`comp-revision-void-${rev.id}`}>
              <X className="w-3 h-3" />Void this revision
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function NewRevisionDialog({ wsId, employeeId, seed, onClose, onSaved }) {
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("annual_raise");
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [comp, setComp] = useState({ ...EMPTY_COMPONENTS, ...(seed || {}) });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setComp((prev) => ({ ...prev, [k]: v }));

  // Live totals mirror the backend `_totals` helper (r-comp-cadence · Feb 2026).
  const totals = useMemo(() => {
    const c = Object.fromEntries(Object.entries(comp).map(([k, v]) => [k, Number(v) || 0]));
    const base = c.basic + c.hra + c.special_allowance + c.bonus_monthly + c.other_allowances;
    const fixedAccrual = (c.quarterly_fixed || 0) / 3 + (c.annual_fixed || 0) / 12;
    const perfAccrual = (c.quarterly_performance_bonus || 0) / 3 + (c.annual_performance_bonus || 0) / 12;
    const gross = base + fixedAccrual;
    const grossOntarget = gross + perfAccrual;
    const deduct = c.employee_pf + c.tds_monthly + (c.professional_tax || 0) + c.other_deductions;
    const net = gross - deduct;
    const employer = gross + c.employer_pf + c.employer_esi;
    const employerOntarget = grossOntarget + c.employer_pf + c.employer_esi;
    const ctc = employerOntarget * 12 + c.gratuity_annual;
    return {
      gross_monthly: gross,
      gross_monthly_ontarget: grossOntarget,
      net_monthly: net,
      employer_cost_monthly: employer,
      employer_cost_monthly_ontarget: employerOntarget,
      annual_ctc: ctc,
      monthly_fixed_accrual: fixedAccrual,
      monthly_perf_accrual: perfAccrual,
    };
  }, [comp]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/employees/${employeeId}/compensation`, {
        employee_id: employeeId,
        effective_from: effectiveFrom,
        reason, notes, currency,
        components: Object.fromEntries(Object.entries(comp).map(([k, v]) => [k, Number(v) || 0])),
      });
      toast.success("Revision saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const numField = (key, label) => (
    <div key={key}>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="0.01" inputMode="decimal" value={comp[key]} onChange={(e) => set(key, e.target.value)} className="h-9 font-mono" data-testid={`comp-input-${key}`} />
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="comp-new-dialog">
        <DialogHeader>
          <DialogTitle>New compensation revision</DialogTitle>
          <DialogDescription>Every save is append-only. The "current" revision is the newest whose <code>effective_from</code> ≤ today.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Effective from *</Label><Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="h-10" data-testid="comp-effective-from" /></div>
            <div><Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-10" data-testid="comp-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{REASON_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="h-10 font-mono" /></div>
            <div className="sm:col-span-3"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Board approval # / promotion memo link / market-adjustment reference…" rows={2} data-testid="comp-notes" /></div>
          </div>

          <div className="pt-3 border-t border-border">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Monthly earnings</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {numField("basic", "Basic")}
              {numField("hra", "HRA")}
              {numField("special_allowance", "Special allowance")}
              {numField("bonus_monthly", "Monthly Performance Bonus")}
              {numField("other_allowances", "Other allowances")}
            </div>
          </div>
          {/* r-comp-cadence · Feb 2026 · Non-monthly earnings.
              Fixed buckets prorate into every month; performance buckets
              only surface in the "on-target" projections. */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">
              Quarterly &amp; annual earnings
              <span className="ml-2 text-[10px] normal-case tracking-normal text-muted-foreground/70 font-normal">
                Fixed spreads into monthly gross · Performance shows in on-target only
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {numField("quarterly_fixed", "Quarterly Fixed")}
              {numField("quarterly_performance_bonus", "Quarterly Performance")}
              {numField("annual_fixed", "Annual Fixed")}
              {numField("annual_performance_bonus", "Annual Performance")}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Employer contributions</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {numField("employer_pf", "Employer PF")}
              {numField("employer_esi", "Employer ESI")}
              {numField("gratuity_annual", "Gratuity (annual)")}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Deductions</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {numField("employee_pf", "Employee PF")}
              {numField("tds_monthly", "TDS (monthly)")}
              {numField("professional_tax", "Professional Tax")}
              {numField("other_deductions", "Other deductions")}
            </div>
          </div>

          <div className="border border-primary/40 bg-primary/5 p-3">
            <div className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-2">Live totals</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><div className="text-[10px] text-muted-foreground uppercase">Gross monthly</div><div className="font-mono font-bold" data-testid="comp-total-gross">{fmtMoney(totals.gross_monthly)}</div></div>
              <div><div className="text-[10px] text-muted-foreground uppercase">Net take-home</div><div className="font-mono font-bold" data-testid="comp-total-net">{fmtMoney(totals.net_monthly)}</div></div>
              <div><div className="text-[10px] text-muted-foreground uppercase">Employer cost / m</div><div className="font-mono font-bold" data-testid="comp-total-employer">{fmtMoney(totals.employer_cost_monthly)}</div></div>
              <div><div className="text-[10px] text-muted-foreground uppercase">Annual CTC (on-target)</div><div className="font-mono font-bold" data-testid="comp-total-ctc">{fmtMoney(totals.annual_ctc)}</div></div>
            </div>
            {(totals.monthly_perf_accrual > 0 || totals.monthly_fixed_accrual > 0) && (
              <div className="mt-2 pt-2 border-t border-primary/20 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">Fixed accrual / m</div>
                  <div className="font-mono text-xs" data-testid="comp-total-fixed-accrual">{fmtMoney(totals.monthly_fixed_accrual)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">On-target perf / m</div>
                  <div className="font-mono text-xs" data-testid="comp-total-perf-accrual">{fmtMoney(totals.monthly_perf_accrual)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">Gross monthly (on-target)</div>
                  <div className="font-mono text-xs" data-testid="comp-total-gross-ontarget">{fmtMoney(totals.gross_monthly_ontarget)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">Employer cost (on-target)</div>
                  <div className="font-mono text-xs" data-testid="comp-total-employer-ontarget">{fmtMoney(totals.employer_cost_monthly_ontarget)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">Cancel</Button>
          <Button onClick={submit} disabled={busy || !effectiveFrom} data-testid="comp-submit" className="w-full sm:w-auto">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save revision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ═════════════════════ Grid (Excel-like) tab ═════════════════════

// Feb 2026 · Component-level columns for the Grid. Each entry defines
// the row-key on the /compensation/grid response and the header label.
const GRID_COMP_COLS = [
  { k: "basic", label: "Basic" },
  { k: "hra", label: "HRA" },
  { k: "special_allowance", label: "Special" },
  { k: "bonus_monthly", label: "Monthly Perf. Bonus" },
  { k: "other_allowances", label: "Other allow." },
  // r-comp-cadence · Feb 2026
  { k: "quarterly_fixed", label: "Qtly Fixed" },
  { k: "quarterly_performance_bonus", label: "Qtly Perf." },
  { k: "annual_fixed", label: "Annual Fixed" },
  { k: "annual_performance_bonus", label: "Annual Perf." },
  { k: "employer_pf", label: "Employer PF" },
  { k: "employer_esi", label: "Employer ESI" },
  { k: "gratuity_annual", label: "Gratuity / yr" },
  { k: "employee_pf", label: "Employee PF" },
  { k: "tds_monthly", label: "TDS" },
  { k: "professional_tax", label: "Prof. Tax" },
  { k: "other_deductions", label: "Other ded." },
  { k: "gross_monthly", label: "Gross", strong: true },
  { k: "net_monthly", label: "Net", strong: true },
  { k: "employer_cost_monthly", label: "Employer cost", strong: true },
  { k: "annual_ctc", label: "Annual CTC", strong: true },
];

function CompensationGrid({ wsId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [depts, setDepts] = useState(new Set());       // multi
  const [desigs, setDesigs] = useState(new Set());     // multi
  const [groupBy, setGroupBy] = useState("none");      // none|department|designation
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [sort, setSort] = useState({ key: "employer_cost_monthly", dir: "desc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/compensation/grid`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load grid")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  const uniqueDepts = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.department_id, r.department_name);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const uniqueDesigs = useMemo(() => {
    const s = new Set(rows.map((r) => r.designation || "—"));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (onlyMissing && r.has_revision) return false;
      if (depts.size > 0 && !depts.has(r.department_id)) return false;
      if (desigs.size > 0 && !desigs.has(r.designation || "—")) return false;
      if (!term) return true;
      return (r.full_name || "").toLowerCase().includes(term)
          || (r.designation || "").toLowerCase().includes(term)
          || (r.department_name || "").toLowerCase().includes(term)
          || (r.email || "").toLowerCase().includes(term);
    });
    const { key, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    out = out.slice().sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mult;
      return String(va || "").localeCompare(String(vb || "")) * mult;
    });
    return out;
  }, [rows, q, depts, desigs, onlyMissing, sort]);

  // Group + subtotal
  const grouped = useMemo(() => {
    if (groupBy === "none") return null;
    const key = groupBy === "department" ? "department_name" : "designation";
    const map = new Map();
    for (const r of filtered) {
      const g = r[key] || "—";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(r);
    }
    return [...map.entries()]
      .map(([name, list]) => {
        const totals = {};
        for (const col of GRID_COMP_COLS) totals[col.k] = list.reduce((s, r) => s + (r[col.k] || 0), 0);
        return { name, list, totals, headcount: list.length };
      })
      .sort((a, b) => (b.totals.employer_cost_monthly || 0) - (a.totals.employer_cost_monthly || 0));
  }, [filtered, groupBy]);

  const grandTotals = useMemo(() => {
    const t = {};
    for (const col of GRID_COMP_COLS) t[col.k] = filtered.reduce((s, r) => s + (r[col.k] || 0), 0);
    return t;
  }, [filtered]);

  const toggleSet = (setter, val) => setter((prev) => {
    const n = new Set(prev);
    if (n.has(val)) n.delete(val); else n.add(val);
    return n;
  });

  const clickHeader = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" });

  const exportView = () => {
    // Reuse the same CSV formatter as CompToolbar so admins can round-trip.
    const headers = ["Employee", "Email", "Department", "Designation", "Effective from",
      ...GRID_COMP_COLS.map((c) => c.label)];
    const lines = [headers.map(_csvCell).join(",")];
    const emit = (r) => {
      const cells = [
        r.full_name, r.email, r.department_name, r.designation, r.effective_from || "",
        ...GRID_COMP_COLS.map((c) => r[c.k] || 0),
      ];
      lines.push(cells.map(_csvCell).join(","));
    };
    if (grouped) {
      for (const g of grouped) {
        lines.push([`${groupBy === "department" ? "Department" : "Designation"}: ${g.name}`].map(_csvCell).join(","));
        for (const r of g.list) emit(r);
        const subTotals = ["Subtotal", "", "", "", `${g.headcount} emp`,
          ...GRID_COMP_COLS.map((c) => g.totals[c.k] || 0)];
        lines.push(subTotals.map(_csvCell).join(","));
      }
    } else {
      for (const r of filtered) emit(r);
    }
    const grand = ["Grand total", "", "", "", `${filtered.length} emp`,
      ...GRID_COMP_COLS.map((c) => grandTotals[c.k] || 0)];
    lines.push(grand.map(_csvCell).join(","));
    _dl(`compensation-grid-${new Date().toISOString().slice(0, 10)}.csv`, lines.join("\r\n"));
    toast.success("Exported current view");
  };

  if (loading) {
    return <Card><CardContent className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading grid…</CardContent></Card>;
  }

  return (
    <div className="space-y-3" data-testid="comp-grid-tab">
      {/* Filter bar */}
      <Card><CardContent className="p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px_auto] gap-2">
          <Input placeholder="Search name / email / designation / department…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="grid-search" />
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-9 text-xs" data-testid="grid-groupby">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No grouping</SelectItem>
              <SelectItem value="department">Group by department</SelectItem>
              <SelectItem value="designation">Group by designation</SelectItem>
            </SelectContent>
          </Select>
          <label className="text-xs flex items-center gap-2 px-3 border border-border h-9" data-testid="grid-missing-toggle">
            <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
            Missing revision only
          </label>
          <Button size="sm" variant="outline" onClick={exportView} data-testid="grid-export"><Download className="w-4 h-4 mr-2" />Export view</Button>
        </div>
        {/* Multi-select chip filters */}
        <div className="flex items-start gap-3 flex-wrap">
          <FilterChipGroup label="Departments" values={uniqueDepts.map((d) => ({ id: d.id, name: d.name }))}
            selected={depts} onToggle={(v) => toggleSet(setDepts, v)}
            testidPrefix="grid-dept" onClear={() => setDepts(new Set())} />
          <FilterChipGroup label="Designations" values={uniqueDesigs.map((d) => ({ id: d, name: d }))}
            selected={desigs} onToggle={(v) => toggleSet(setDesigs, v)}
            testidPrefix="grid-desig" onClear={() => setDesigs(new Set())} />
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
          <span><strong className="text-foreground">{filtered.length}</strong> of {rows.length} employees</span>
          <span>·</span>
          <span>Employer cost: <strong className="font-mono text-foreground">{fmtMoney(grandTotals.employer_cost_monthly)}</strong> / month</span>
          <span>·</span>
          <span>Annual CTC: <strong className="font-mono text-foreground">{fmtMoney(grandTotals.annual_ctc)}</strong></span>
        </div>
      </CardContent></Card>

      {/* Data table — bounded height + both-axis scroll so both the thead
          and the leftmost employee column can stick within the table box.
          `top-0` / `left-0` are relative to the scroll container. */}
      <Card><CardContent className="p-0">
        <div className="overflow-auto max-h-[calc(100vh-260px)]">
          <table className="w-full text-xs" data-testid="comp-grid-table">
            <thead className="bg-muted sticky top-0 z-20 shadow-sm">
              <tr className="border-b border-border">
                <SortableHeader label="Employee" k="full_name" sort={sort} onClick={clickHeader} align="left" sticky />
                <SortableHeader label="Department" k="department_name" sort={sort} onClick={clickHeader} align="left" />
                <SortableHeader label="Designation" k="designation" sort={sort} onClick={clickHeader} align="left" />
                {GRID_COMP_COLS.map((c) => (
                  <SortableHeader key={c.k} label={c.label} k={c.k} sort={sort} onClick={clickHeader} align="right" strong={c.strong} />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {grouped ? grouped.map((g) => (
                <GridGroup key={g.name} group={g} groupByLabel={groupBy === "department" ? "Dept" : "Role"} />
              )) : filtered.map((r) => <GridRow key={r.employee_id} r={r} />)}
              {filtered.length === 0 && (
                <tr><td colSpan={3 + GRID_COMP_COLS.length} className="p-8 text-center text-muted-foreground">
                  No employees match the current filters.
                </td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-primary/5 border-t-2 border-primary/30 sticky bottom-0">
                <tr className="font-semibold" data-testid="comp-grid-grand-total">
                  <td className="px-3 py-2 text-primary uppercase text-[10px] tracking-widest">Grand total</td>
                  <td className="px-3 py-2 text-muted-foreground text-[10px]">{filtered.length} emp</td>
                  <td className="px-3 py-2"></td>
                  {GRID_COMP_COLS.map((c) => (
                    <td key={c.k} className={`text-right px-3 py-2 font-mono ${c.strong ? "text-primary" : ""}`}>
                      {fmtMoney(grandTotals[c.k] || 0)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function FilterChipGroup({ label, values, selected, onToggle, testidPrefix, onClear }) {
  if (values.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        {selected.size > 0 && (
          <button type="button" onClick={onClear} className="text-[10px] text-primary hover:underline" data-testid={`${testidPrefix}-clear`}>
            Clear ({selected.size})
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 flex-wrap max-w-full">
        {values.map((v) => {
          const on = selected.has(v.id);
          return (
            <button key={v.id} type="button"
              onClick={() => onToggle(v.id)}
              className={`text-[11px] px-2 py-0.5 rounded-none border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/50"}`}
              data-testid={`${testidPrefix}-${String(v.id).replace(/\s+/g, "_")}`}
              title={v.name}
            >{v.name}</button>
          );
        })}
      </div>
    </div>
  );
}

function SortableHeader({ label, k, sort, onClick, align = "right", strong = false, sticky = false }) {
  const active = sort.key === k;
  return (
    <th className={`px-3 py-2 text-${align} text-[10px] uppercase tracking-widest cursor-pointer select-none whitespace-nowrap ${strong ? "text-primary" : "text-muted-foreground"} hover:text-foreground ${sticky ? "sticky left-0 bg-muted z-30" : ""}`}
      onClick={() => onClick(k)}>
      {label}{active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

function GridRow({ r }) {
  return (
    <tr className={`hover:bg-muted/30 ${!r.has_revision ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}`} data-testid={`comp-grid-row-${r.employee_id}`}>
      <td className={`px-3 py-2 whitespace-nowrap sticky left-0 z-10 ${!r.has_revision ? "bg-amber-50/80 dark:bg-amber-950/20" : "bg-background"}`}>
        <div className="font-semibold truncate max-w-[220px]">{r.full_name}</div>
        {!r.has_revision && <div className="text-[10px] text-amber-700 dark:text-amber-500 italic">no revision yet</div>}
      </td>
      <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">{r.department_name}</td>
      <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">{r.designation}</td>
      {GRID_COMP_COLS.map((c) => (
        <td key={c.k} className={`text-right px-3 py-2 font-mono whitespace-nowrap ${c.strong ? "font-semibold" : ""} ${(r[c.k] || 0) === 0 ? "text-muted-foreground/60" : ""}`}>
          {fmtMoney(r[c.k] || 0)}
        </td>
      ))}
    </tr>
  );
}

function GridGroup({ group, groupByLabel }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr className="bg-slate-100 dark:bg-slate-800/50 border-t border-border font-semibold text-[11px]" data-testid={`comp-grid-group-${group.name}`}>
        <td className="px-3 py-2" colSpan={3}>
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 hover:text-primary">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
            <span className="uppercase text-[10px] tracking-widest text-muted-foreground">{groupByLabel}</span>
            <span>{group.name}</span>
            <span className="text-muted-foreground font-normal ml-1">({group.headcount})</span>
          </button>
        </td>
        {GRID_COMP_COLS.map((c) => (
          <td key={c.k} className={`text-right px-3 py-2 font-mono ${c.strong ? "text-primary" : "text-muted-foreground"}`}>
            {fmtMoney(group.totals[c.k] || 0)}
          </td>
        ))}
      </tr>
      {open && group.list.map((r) => <GridRow key={r.employee_id} r={r} />)}
    </>
  );
}


// ═════════════════════ AI Intelligence tab ═════════════════════

const PIE_COLORS = ["#FF4500", "#FFA500", "#FFD700", "#4CAF50", "#00BCD4", "#3F51B5", "#9C27B0", "#E91E63", "#795548", "#607D8B"];
const COMPONENT_LABELS = {
  basic: "Basic",
  hra: "HRA",
  special_allowance: "Special Allow.",
  bonus_monthly: "Monthly Perf. Bonus",
  other_allowances: "Other Allow.",
  // r-comp-cadence · Feb 2026 · Prorated-to-monthly views
  quarterly_fixed_monthly: "Quarterly Fixed (÷3)",
  quarterly_perf_monthly: "Quarterly Perf. (÷3)",
  annual_fixed_monthly: "Annual Fixed (÷12)",
  annual_perf_monthly: "Annual Perf. (÷12)",
  employer_pf: "Employer PF",
  employer_esi: "Employer ESI",
  gratuity_monthly: "Gratuity (mo)",
};

function CompensationInsights({ wsId, onJumpToEmployee }) {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [insightsRes, alertsRes] = await Promise.all([
        api.get(`/workspaces/${wsId}/compensation/insights`, { params: { months } }),
        api.get(`/workspaces/${wsId}/compensation/alerts`, { params: { months, stagnation_months: 18 } }),
      ]);
      setData(insightsRes.data);
      setAlerts(alertsRes.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load insights")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId, months]);

  if (loading || !data) {
    return <Card><CardContent className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Crunching your compensation data…</CardContent></Card>;
  }

  const componentPieData = (data.component_split || [])
    .map((c) => ({ name: COMPONENT_LABELS[c.component] || c.component, value: c.value }));
  const deptBarData = (data.by_department || []).map((d) => ({
    name: d.department_name.length > 18 ? d.department_name.slice(0, 16) + "…" : d.department_name,
    fullName: d.department_name,
    cost: d.cost,
    headcount: d.headcount,
    avg: d.headcount ? d.cost / d.headcount : 0,
  }));
  const desigBarData = (data.by_designation || []).map((d) => ({
    name: d.designation.length > 18 ? d.designation.slice(0, 16) + "…" : d.designation,
    fullName: d.designation,
    cost: d.cost,
    headcount: d.headcount,
    avg: d.headcount ? d.cost / d.headcount : 0,
  }));
  const timelineData = (data.cost_timeline || []).map((t) => ({
    name: t.month.slice(2),   // "26-01" — compact
    cost: t.employer_cost_monthly,
    headcount: t.headcount,
    net: t.net_monthly,
  }));
  const firstCost = timelineData[0]?.cost || 0;
  const lastCost = timelineData[timelineData.length - 1]?.cost || 0;
  const trend = firstCost > 0 ? ((lastCost - firstCost) / firstCost) * 100 : 0;

  return (
    <div className="space-y-4" data-testid="comp-insights-tab">
      {/* Controls + KPIs */}
      <Card><CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-5 h-5 text-primary" />
            <div>
              <div className="text-sm font-bold">Compensation intelligence</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Snapshot · {data.as_of}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Look back:</Label>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="insights-months">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
                <SelectItem value="24">24 months</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} data-testid="insights-refresh">
              <TrendingUp className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <InsightKpi label="Departments" value={String(data.by_department.length)} icon={<Building2 className="w-3.5 h-3.5" />} testid="insight-kpi-depts" />
          <InsightKpi label="Distinct roles" value={String(data.by_designation.length)} icon={<Users2 className="w-3.5 h-3.5" />} testid="insight-kpi-roles" />
          <InsightKpi label="Current employer cost" value={fmtMoney(lastCost)} sub={`${timelineData[timelineData.length - 1]?.headcount || 0} employees`} testid="insight-kpi-now" />
          <InsightKpi label={`Change (${months} mo)`}
            value={`${trend > 0 ? "+" : ""}${trend.toFixed(1)}%`}
            sub={fmtMoney(lastCost - firstCost)}
            tone={trend > 0 ? "emerald" : trend < 0 ? "red" : "neutral"}
            testid="insight-kpi-trend" />
        </div>
      </CardContent></Card>

      {/* HR intelligence signals — actionable lists derived from history */}
      {alerts && <SignalsPanel alerts={alerts} months={months} onJumpToEmployee={onJumpToEmployee} />}

      {/* Cost timeline */}
      <Card><CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-primary" />
          <div className="text-sm font-semibold">Employer cost — last {months} months</div>
        </div>
        {timelineData.length > 0 ? (
          <div style={{ width: "100%", height: 260 }} data-testid="insights-timeline-chart">
            <ResponsiveContainer>
              <AreaChart data={timelineData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF4500" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#FF4500" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} />
                <RTooltip
                  formatter={(v, name) => name === "cost" ? [fmtMoney(v), "Employer cost"] : name === "net" ? [fmtMoney(v), "Net"] : [v, name]}
                  labelFormatter={(l) => `20${l}`}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="cost" stroke="#FF4500" fill="url(#costGrad)" strokeWidth={2} name="Employer cost" />
                <Line type="monotone" dataKey="net" stroke="#4CAF50" strokeWidth={2} dot={false} name="Net take-home" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : <div className="text-xs text-muted-foreground italic p-8 text-center">No timeline data yet.</div>}
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cost by department */}
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-primary" />
            <div className="text-sm font-semibold">Monthly cost by department</div>
          </div>
          {deptBarData.length > 0 ? (
            <div style={{ width: "100%", height: Math.max(220, deptBarData.length * 38) }} data-testid="insights-dept-chart">
              <ResponsiveContainer>
                <BarChart data={deptBarData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                  <RTooltip formatter={(v, _n, p) => [fmtMoney(v), `${p.payload.fullName} · ${p.payload.headcount} emp · avg ${fmtMoney(p.payload.avg)}`]} />
                  <Bar dataKey="cost" fill="#FF4500" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-xs text-muted-foreground italic p-8 text-center">No department data.</div>}
        </CardContent></Card>

        {/* Component split pie */}
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <PieChartIcon className="w-4 h-4 text-primary" />
            <div className="text-sm font-semibold">Component split (monthly)</div>
          </div>
          {componentPieData.length > 0 ? (
            <div style={{ width: "100%", height: 300 }} data-testid="insights-component-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={componentPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={100} paddingAngle={2}>
                    {componentPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="text-xs text-muted-foreground italic p-8 text-center">No components to break down.</div>}
        </CardContent></Card>
      </div>

      {/* Cost by role (top 15) */}
      <Card><CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users2 className="w-4 h-4 text-primary" />
          <div className="text-sm font-semibold">Top roles by monthly cost</div>
        </div>
        {desigBarData.length > 0 ? (
          <div style={{ width: "100%", height: Math.max(220, desigBarData.length * 32) }} data-testid="insights-desig-chart">
            <ResponsiveContainer>
              <BarChart data={desigBarData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                <RTooltip formatter={(v, _n, p) => [fmtMoney(v), `${p.payload.fullName} · ${p.payload.headcount} emp · avg ${fmtMoney(p.payload.avg)}`]} />
                <Bar dataKey="cost" fill="#3F51B5" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <div className="text-xs text-muted-foreground italic p-8 text-center">No role data yet.</div>}
      </CardContent></Card>

      {/* Feb 2026 · Cost breakdown table (grouped by dept/designation/none) */}
      <CostBreakdownCard wsId={wsId} onJumpToEmployee={onJumpToEmployee} />

      {/* Feb 2026 · Employees × months projection matrix */}
      <MonthlyProjectionMatrix wsId={wsId} />

      {/* Feb 2026 · 24-month cost projection */}
      <ProjectionCard
        timelineData={timelineData}
        wsId={wsId}
        byDepartment={data.by_department || []}
        byDesignation={data.by_designation || []}
      />
    </div>
  );
}

function InsightKpi({ label, value, sub, icon, tone, testid }) {
  const toneCls = tone === "emerald" ? "text-emerald-600" : tone === "red" ? "text-red-600" : "";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {icon}{label}
      </div>
      <div className={`text-lg font-bold mt-1 font-mono ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}


// Feb 2026 · HR intelligence signals panel — top raises, stagnation flags,
// role-band anomalies + sharp-change (typo/outlier) alerts. Everything is
// derived from compensation revisions so no additional data collection is
// required. Empty lists render a friendly "nothing to worry about" hint.

// Feb 2026 · Tabular by-employee actual costs, grouped by department or
// designation with per-group subtotals + a grand-total footer. Reuses the
// existing `/compensation/grid` endpoint so there's a single source of
// truth for per-employee current cost.
function CostBreakdownCard({ wsId, onJumpToEmployee }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("department"); // none|department|designation

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/workspaces/${wsId}/compensation/grid`);
        setRows(data.rows || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load breakdown")); }
      finally { setLoading(false); }
    })();
  }, [wsId]);

  const grand = useMemo(() => rows.reduce((s, r) => s + (r.employer_cost_monthly || 0), 0), [rows]);

  const grouped = useMemo(() => {
    if (groupBy === "none") return null;
    const key = groupBy === "department" ? "department_name" : "designation";
    const m = new Map();
    for (const r of rows) {
      if (!r.has_revision) continue;
      const g = r[key] || "—";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(r);
    }
    return [...m.entries()]
      .map(([name, list]) => {
        const total = list.reduce((s, r) => s + (r.employer_cost_monthly || 0), 0);
        const annual = list.reduce((s, r) => s + (r.annual_ctc || 0), 0);
        return { name, list, total, annual, headcount: list.length };
      })
      .sort((a, b) => b.total - a.total);
  }, [rows, groupBy]);

  return (
    <Card data-testid="comp-breakdown-card"><CardContent className="p-0">
      <div className="p-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TableIcon className="w-4 h-4 text-primary" />
          <div className="text-sm font-semibold">Cost breakdown by employee</div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Group by:</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="breakdown-groupby">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Flat (no grouping)</SelectItem>
              <SelectItem value="department">By department</SelectItem>
              <SelectItem value="designation">By designation</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : (
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-xs" data-testid="breakdown-table">
            <thead className="bg-muted sticky top-0 z-10">
              <tr className="border-b border-border">
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Employee / Group</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Department</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Designation</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Employer cost / mo</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">% of total</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Annual CTC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {grouped ? grouped.map((g) => (
                <BreakdownGroup key={g.name} g={g} groupByLabel={groupBy === "department" ? "Dept" : "Role"} grand={grand} onJumpToEmployee={onJumpToEmployee} />
              )) : rows.filter((r) => r.has_revision).sort((a, b) => b.employer_cost_monthly - a.employer_cost_monthly).map((r) => (
                <BreakdownRow key={r.employee_id} r={r} grand={grand} onJumpToEmployee={onJumpToEmployee} />
              ))}
            </tbody>
            <tfoot className="bg-background border-t-2 border-primary/60 sticky bottom-0 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.08)]">
              <tr className="font-semibold" data-testid="breakdown-grand-total">
                <td className="px-3 py-2 text-primary uppercase text-[10px] tracking-widest">Grand total</td>
                <td className="px-3 py-2 text-muted-foreground text-[10px]">{rows.filter((r) => r.has_revision).length} emp</td>
                <td className="px-3 py-2"></td>
                <td className="text-right px-3 py-2 font-mono text-primary">{fmtMoney(grand)}</td>
                <td className="text-right px-3 py-2 font-mono text-primary">100%</td>
                <td className="text-right px-3 py-2 font-mono text-primary">{fmtMoney(grand * 12)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </CardContent></Card>
  );
}

function BreakdownRow({ r, grand, onJumpToEmployee }) {
  const pct = grand > 0 ? (r.employer_cost_monthly / grand) * 100 : 0;
  return (
    <tr className="hover:bg-muted/40 cursor-pointer" onClick={() => onJumpToEmployee && onJumpToEmployee(r.employee_id)}
        data-testid={`breakdown-row-${r.employee_id}`}>
      <td className="px-3 py-2 font-semibold">{r.full_name}</td>
      <td className="px-3 py-2 text-muted-foreground">{r.department_name}</td>
      <td className="px-3 py-2 text-muted-foreground">{r.designation}</td>
      <td className="text-right px-3 py-2 font-mono">{fmtMoney(r.employer_cost_monthly)}</td>
      <td className="text-right px-3 py-2 font-mono">
        <div className="flex items-center gap-1 justify-end">
          <div className="h-1.5 bg-muted w-16 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <span className="text-[10px] min-w-[36px] text-right">{pct.toFixed(1)}%</span>
        </div>
      </td>
      <td className="text-right px-3 py-2 font-mono">{fmtMoney(r.annual_ctc)}</td>
    </tr>
  );
}

function BreakdownGroup({ g, groupByLabel, grand, onJumpToEmployee }) {
  const [open, setOpen] = useState(true);
  const pct = grand > 0 ? (g.total / grand) * 100 : 0;
  return (
    <>
      <tr className="bg-slate-100 dark:bg-slate-800/50 border-t border-border font-semibold" data-testid={`breakdown-group-${g.name}`}>
        <td className="px-3 py-2" colSpan={3}>
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs hover:text-primary">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
            <span className="uppercase text-[10px] tracking-widest text-muted-foreground">{groupByLabel}</span>
            <span>{g.name}</span>
            <span className="text-muted-foreground font-normal ml-1">({g.headcount})</span>
          </button>
        </td>
        <td className="text-right px-3 py-2 font-mono text-primary">{fmtMoney(g.total)}</td>
        <td className="text-right px-3 py-2 font-mono text-primary">
          <div className="flex items-center gap-1 justify-end">
            <div className="h-1.5 bg-muted w-16 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="text-[10px] min-w-[36px] text-right">{pct.toFixed(1)}%</span>
          </div>
        </td>
        <td className="text-right px-3 py-2 font-mono text-primary">{fmtMoney(g.annual)}</td>
      </tr>
      {open && g.list.map((r) => <BreakdownRow key={r.employee_id} r={r} grand={grand} onJumpToEmployee={onJumpToEmployee} />)}
    </>
  );
}


// Feb 2026 · Employees-as-rows × months-as-columns projection matrix.
// Groups by None / Department / Designation / Status with per-group
// subtotals and a sticky column-total footer. Onboarding cells before
// join date show "—", offboarding cells after LWD show "—".
function MonthlyProjectionMatrix({ wsId }) {
  const [months, setMonths] = useState(12);
  const [hikePct, setHikePct] = useState(8);
  const [groupBy, setGroupBy] = useState("department");   // none|department|designation|status
  const [statusFilter, setStatusFilter] = useState("all"); // all|active|onboarding|offboarding
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Feb 2026 · click-to-highlight state — row = employee_id, col = month index
  const [highRow, setHighRow] = useState(null);
  const [highCol, setHighCol] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/compensation/monthly-projection`,
          { params: { months, hike_pct: hikePct } });
        setData(data);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load projection")); }
      finally { setLoading(false); }
    })();
  }, [wsId, months, hikePct]);

  const rows = data?.employees || [];
  const monthLabels = data?.months || [];
  const headcountByMonth = data?.headcount_by_month || [];

  // Feb 2026 · Rolling headcount + payout curve for the same window.
  const trendData = useMemo(() => {
    return monthLabels.map((ml, i) => ({
      name: ml,
      headcount: headcountByMonth[i] || 0,
      payout: data?.column_totals?.[i] || 0,
    }));
  }, [monthLabels, headcountByMonth, data]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const groups = useMemo(() => {
    if (groupBy === "none") return null;
    const keyFn = (r) => {
      if (groupBy === "department") return r.department_name;
      if (groupBy === "designation") return r.designation;
      return r.status;
    };
    const m = new Map();
    for (const r of filtered) {
      const k = keyFn(r);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return [...m.entries()].map(([name, list]) => {
      const subtotals = monthLabels.map((_, i) =>
        list.reduce((s, r) => s + (r.monthly_gross[i] || 0), 0));
      const rowTotal = list.reduce((s, r) => s + r.row_total, 0);
      return { name, list, subtotals, rowTotal };
    }).sort((a, b) => b.rowTotal - a.rowTotal);
  }, [filtered, groupBy, monthLabels]);

  const columnTotals = useMemo(() => {
    return monthLabels.map((_, i) =>
      filtered.reduce((s, r) => s + (r.monthly_gross[i] || 0), 0));
  }, [filtered, monthLabels]);
  const grandTotal = columnTotals.reduce((s, v) => s + v, 0);

  const exportCsv = () => {
    const headers = ["Employee", "Department", "Designation", "Status", ...monthLabels, "Row total"];
    const lines = [headers.map(_csvCell).join(",")];
    const emit = (r) => lines.push([
      r.full_name, r.department_name, r.designation, r.status,
      ...r.monthly_gross.map((v) => v || 0),
      r.row_total,
    ].map(_csvCell).join(","));
    if (groups) {
      for (const g of groups) {
        lines.push([`${groupBy}: ${g.name}`].map(_csvCell).join(","));
        for (const r of g.list) emit(r);
        lines.push(["Subtotal", "", "", `${g.list.length} emp`, ...g.subtotals, g.rowTotal].map(_csvCell).join(","));
      }
    } else {
      for (const r of filtered) emit(r);
    }
    lines.push(["Grand total", "", "", `${filtered.length} emp`, ...columnTotals, grandTotal].map(_csvCell).join(","));
    _dl(`comp-monthly-projection-${new Date().toISOString().slice(0, 10)}.csv`, lines.join("\r\n"));
    toast.success("Exported matrix");
  };

  return (
    <Card data-testid="comp-monthly-matrix"><CardContent className="p-0">
      <div className="p-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TableIcon className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-semibold">Monthly payout projection</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Employees × months · gross per row</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="text-[10px] whitespace-nowrap">Hike %</Label>
            <input type="number" min="0" max="30" step="0.5" value={hikePct}
              onChange={(e) => setHikePct(Number(e.target.value) || 0)}
              className="h-8 w-14 text-xs font-mono border border-border px-2 bg-background focus:outline-none focus:border-primary"
              data-testid="matrix-hike" />
          </div>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="h-8 w-24 text-xs" data-testid="matrix-months"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">6 mo</SelectItem>
              <SelectItem value="12">12 mo</SelectItem>
              <SelectItem value="24">24 mo</SelectItem>
              <SelectItem value="36">36 mo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="matrix-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No grouping</SelectItem>
              <SelectItem value="department">Group by department</SelectItem>
              <SelectItem value="designation">Group by designation</SelectItem>
              <SelectItem value="status">Group by status</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="matrix-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="onboarding">Onboarding only</SelectItem>
              <SelectItem value="offboarding">Offboarding only</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="matrix-export"><Download className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {loading ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin" />Building matrix…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-xs italic text-muted-foreground text-center">No projectable employees — add compensation revisions first.</div>
      ) : (
        <>
        {/* Rolling headcount + payout curve. Payout on left Y-axis, headcount
            on right. Boards immediately see "we're growing from N → M people". */}
        {trendData.length > 1 && (
          <div className="px-4 pt-4 pb-2 border-b border-border" data-testid="matrix-trend-chart">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Headcount → {headcountByMonth[headcountByMonth.length - 1] || 0} by {monthLabels[monthLabels.length - 1]}
                <span className="ml-3 normal-case tracking-normal text-muted-foreground font-normal">
                  · Start: {headcountByMonth[0] || 0} · End: {headcountByMonth[headcountByMonth.length - 1] || 0}
                  · Δ: <strong className={(headcountByMonth[headcountByMonth.length - 1] - headcountByMonth[0]) > 0 ? "text-emerald-700" : "text-red-700"}>{(headcountByMonth[headcountByMonth.length - 1] - headcountByMonth[0]) >= 0 ? "+" : ""}{(headcountByMonth[headcountByMonth.length - 1] - headcountByMonth[0])}</strong>
                </span>
              </div>
            </div>
            <div style={{ width: "100%", height: 180 }}>
              <ResponsiveContainer>
                <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={Math.floor(trendData.length / 12) || 0} />
                  <YAxis yAxisId="payout" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} orientation="left" />
                  <YAxis yAxisId="hc" tick={{ fontSize: 10 }} orientation="right" />
                  <RTooltip
                    formatter={(v, name) => name === "Payout" ? [fmtMoney(v), name] : [v, name]}
                    labelFormatter={(l) => `20${l}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="payout" type="monotone" dataKey="payout" stroke="#FF4500" strokeWidth={2} dot={false} name="Payout" />
                  <Line yAxisId="hc" type="monotone" dataKey="headcount" stroke="#3F51B5" strokeWidth={2} dot={{ r: 2 }} name="Headcount" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <div className="overflow-auto max-h-[600px]">
          <table className="text-xs border-collapse" data-testid="matrix-table" style={{ minWidth: "100%" }}>
            <thead className="bg-muted sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground sticky left-0 bg-muted z-30 min-w-[220px] border-r border-border">Employee</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground min-w-[80px] border-r border-border">Status</th>
                {monthLabels.map((ml, i) => (
                  <th key={ml}
                    onClick={() => setHighCol((c) => c === i ? null : i)}
                    className={`text-right px-3 py-2 text-[10px] uppercase tracking-widest whitespace-nowrap font-mono border-r border-border cursor-pointer select-none hover:text-foreground ${highCol === i ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                    data-testid={`matrix-col-${ml}`}>{ml}</th>
                ))}
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-primary whitespace-nowrap sticky right-0 bg-muted z-30 border-l border-border">Row total</th>
              </tr>
            </thead>
            <tbody>
              {groups ? groups.map((g) => (
                <MatrixGroup key={g.name} g={g} groupByLabel={groupBy} monthLabels={monthLabels}
                  highRow={highRow} highCol={highCol}
                  onRowClick={(eid) => setHighRow((r) => r === eid ? null : eid)}
                  onColClick={(i) => setHighCol((c) => c === i ? null : i)} />
              )) : filtered.map((r) => (
                <MatrixEmpRow key={r.employee_id} r={r} monthLabels={monthLabels}
                  highRow={highRow} highCol={highCol}
                  onRowClick={(eid) => setHighRow((v) => v === eid ? null : eid)}
                  onColClick={(i) => setHighCol((c) => c === i ? null : i)} />
              ))}
            </tbody>
            <tfoot className="bg-background border-t-2 border-primary/60 sticky bottom-0 z-20 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.08)]">
              <tr className="font-semibold" data-testid="matrix-column-totals">
                <td className="text-primary uppercase text-[10px] tracking-widest px-3 py-2 sticky left-0 bg-background z-30 border-r border-border">Grand total ({filtered.length} emp)</td>
                <td className="px-3 py-2 border-r border-border"></td>
                {columnTotals.map((v, i) => (
                  <td key={i}
                    onClick={() => setHighCol((c) => c === i ? null : i)}
                    className={`text-right px-3 py-2 font-mono text-primary whitespace-nowrap border-r border-border cursor-pointer ${highCol === i ? "bg-primary/20" : ""}`}>{fmtMoney(v)}</td>
                ))}
                <td className="text-right px-3 py-2 font-mono text-primary whitespace-nowrap sticky right-0 bg-background z-30 border-l border-border">{fmtMoney(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        </>
      )}
    </CardContent></Card>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Active" },
    onboarding: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Onboarding" },
    offboarding: { cls: "bg-red-50 text-red-700 border-red-200", label: "Offboarding" },
  }[status] || { cls: "bg-muted text-muted-foreground border-border", label: status };
  return <span className={`inline-block text-[9px] px-1.5 py-0.5 border ${cfg.cls} uppercase tracking-widest`}>{cfg.label}</span>;
}

function MatrixEmpRow({ r, monthLabels, highRow, highCol, onRowClick, onColClick }) {
  const rowHi = highRow === r.employee_id;
  return (
    <tr className={`border-t border-border ${rowHi ? "bg-primary/10" : "hover:bg-muted/30"}`} data-testid={`matrix-row-${r.employee_id}`}>
      <td onClick={() => onRowClick && onRowClick(r.employee_id)}
        className={`px-3 py-1.5 whitespace-nowrap sticky left-0 z-10 border-r border-border cursor-pointer ${rowHi ? "bg-primary/10" : "bg-background"}`}>
        <div className="font-semibold truncate max-w-[220px]">{r.full_name}</div>
        <div className="text-[10px] text-muted-foreground truncate">{r.designation} · {r.department_name}</div>
      </td>
      <td className={`px-3 py-1.5 border-r border-border ${rowHi ? "bg-primary/10" : ""}`}><StatusBadge status={r.status} /></td>
      {monthLabels.map((ml, i) => {
        const v = r.monthly_gross[i] || 0;
        const zero = v === 0;
        const colHi = highCol === i;
        const cellHi = rowHi && colHi;
        return (
          <td key={ml}
            onClick={() => onColClick && onColClick(i)}
            className={`text-right px-3 py-1.5 font-mono whitespace-nowrap border-r border-border cursor-pointer ${cellHi ? "bg-primary/30 font-bold" : colHi ? "bg-primary/15" : rowHi ? "bg-primary/10" : ""} ${zero ? "text-muted-foreground/40" : ""}`}>
            {zero ? "—" : fmtMoney(v)}
          </td>
        );
      })}
      <td className={`text-right px-3 py-1.5 font-mono font-semibold sticky right-0 z-10 border-l border-border ${rowHi ? "bg-primary/10" : "bg-background"}`}>{fmtMoney(r.row_total)}</td>
    </tr>
  );
}

function MatrixGroup({ g, groupByLabel, monthLabels, highRow, highCol, onRowClick, onColClick }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr className="bg-slate-100 dark:bg-slate-800/70 border-t border-b border-border font-semibold" data-testid={`matrix-group-${g.name}`}>
        <td className="px-3 py-2 sticky left-0 z-10 bg-slate-100 dark:bg-slate-800/70 border-r border-border" colSpan={2}>
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs hover:text-primary">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
            <span className="uppercase text-[10px] tracking-widest text-muted-foreground">{groupByLabel}</span>
            <span>{g.name}</span>
            <span className="text-muted-foreground font-normal ml-1">({g.list.length})</span>
          </button>
        </td>
        {g.subtotals.map((v, i) => (
          <td key={i}
            onClick={() => onColClick && onColClick(i)}
            className={`text-right px-3 py-2 font-mono text-primary whitespace-nowrap border-r border-border cursor-pointer ${highCol === i ? "bg-primary/20" : ""}`}>{fmtMoney(v)}</td>
        ))}
        <td className="text-right px-3 py-2 font-mono text-primary whitespace-nowrap sticky right-0 bg-slate-100 dark:bg-slate-800/70 z-10 border-l border-border">{fmtMoney(g.rowTotal)}</td>
      </tr>
      {open && g.list.map((r) => (
        <MatrixEmpRow key={r.employee_id} r={r} monthLabels={monthLabels}
          highRow={highRow} highCol={highCol}
          onRowClick={onRowClick} onColClick={onColClick} />
      ))}
    </>
  );
}




// Feb 2026 · Straight-line projection of employer cost forward N months.
// Layers in:
//   • annual hike (compounded monthly)
//   • attrition — workspace-wide OR per-department/per-designation cohorts
//   • scheduled joiners / leavers (from /compensation/headcount-plan)
//   • open requisitions ramped in at their expected joining month
// Named scenarios save/load a full parameter combo so HR and CFO can
// share the exact same "what-if" projection.
function ProjectionCard({ timelineData, wsId, byDepartment, byDesignation }) {
  const [hikePct, setHikePct] = useState(8);
  const [horizon, setHorizon] = useState(24);
  const [attritionMode, setAttritionMode] = useState("workspace"); // workspace|department|designation
  const [attritionPct, setAttritionPct] = useState(0);   // workspace-wide
  const [attritionByDept, setAttritionByDept] = useState({}); // dept_id -> pct
  const [attritionByDesig, setAttritionByDesig] = useState({}); // desig -> pct
  const [includePlan, setIncludePlan] = useState(true);
  const [plan, setPlan] = useState(null);
  // Scenario state
  const [scenarios, setScenarios] = useState([]);
  const [selScenarioId, setSelScenarioId] = useState("");
  const [compareScenarioId, setCompareScenarioId] = useState(""); // Feb 2026 · overlay
  const [dirty, setDirty] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const loadScenarios = async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/compensation/scenarios`);
      setScenarios(data.scenarios || []);
    } catch (e) { /* silent — scenarios are optional */ }
  };
  useEffect(() => { loadScenarios(); /* eslint-disable-next-line */ }, [wsId]);

  // Load the headcount plan whenever the toggle flips on or horizon changes.
  useEffect(() => {
    if (!wsId || !includePlan) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/compensation/headcount-plan`,
          { params: { months: horizon } });
        setPlan(data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Failed to load hiring plan"));
      }
    })();
  }, [wsId, includePlan, horizon]);

  // Any control change marks the current scenario dirty (so the user knows
  // to "Save changes" or "Save as new"). This resets when a scenario loads.
  useEffect(() => { setDirty(true); }, [hikePct, horizon, attritionMode, attritionPct, attritionByDept, attritionByDesig, includePlan]);

  const loadScenario = (id) => {
    const s = scenarios.find((x) => x.id === id);
    if (!s) return;
    setHikePct(s.hike_pct ?? 8);
    setHorizon(s.horizon_months ?? 24);
    setIncludePlan(s.include_hiring_plan !== false);
    setAttritionMode(s.attrition_mode || "workspace");
    setAttritionPct(s.attrition_workspace_pct ?? 0);
    setAttritionByDept(s.attrition_by_department || {});
    setAttritionByDesig(s.attrition_by_designation || {});
    setSelScenarioId(id);
    setTimeout(() => setDirty(false), 50);
    toast.success(`Loaded scenario "${s.name}"`);
  };

  const currentPayload = () => ({
    name: "",   // filled in save dialog
    hike_pct: hikePct,
    horizon_months: horizon,
    include_hiring_plan: includePlan,
    attrition_mode: attritionMode,
    attrition_workspace_pct: attritionPct,
    attrition_by_department: attritionByDept,
    attrition_by_designation: attritionByDesig,
  });

  const overwriteScenario = async () => {
    if (!selScenarioId) return;
    const s = scenarios.find((x) => x.id === selScenarioId);
    if (!s) return;
    try {
      const { data } = await api.put(`/compensation-scenarios/${selScenarioId}`, {
        ...currentPayload(), name: s.name, notes: s.notes,
      });
      setScenarios((rows) => rows.map((r) => r.id === data.id ? data : r));
      setDirty(false);
      toast.success(`Saved changes to "${s.name}"`);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const deleteScenario = async () => {
    if (!selScenarioId) return;
    const s = scenarios.find((x) => x.id === selScenarioId);
    if (!s) return;
    if (!window.confirm(`Delete scenario "${s.name}"?`)) return;
    try {
      await api.delete(`/compensation-scenarios/${selScenarioId}`);
      setScenarios((rows) => rows.filter((r) => r.id !== selScenarioId));
      setSelScenarioId("");
      toast.success("Scenario deleted");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const lastActual = timelineData[timelineData.length - 1];
  const wsCurrentCost = lastActual?.cost || 0;

  // Effective attrition per bucket (workspace | dept | designation) so the
  // month-loop below can look up the right decay factor without branches.
  const cohortDecayFactor = useMemo(() => {
    if (attritionMode === "workspace") {
      const factor = 1 - attritionPct / 100;
      return { total: factor };   // one bucket = whole workspace
    }
    if (attritionMode === "department") {
      const out = { _rest: 1 };
      for (const d of byDepartment) {
        const pct = attritionByDept[d.department_id] ?? attritionByDept[d.department_name] ?? 0;
        out[d.department_id] = { factor: 1 - pct / 100, cost: d.cost };
      }
      return out;
    }
    // designation
    const out = {};
    for (const d of byDesignation) {
      const pct = attritionByDesig[d.designation] ?? 0;
      out[d.designation] = { factor: 1 - pct / 100, cost: d.cost };
    }
    return out;
  }, [attritionMode, attritionPct, attritionByDept, attritionByDesig, byDepartment, byDesignation]);

  const merged = useMemo(() => {
    if (!lastActual) return [];
    const hikeFactor = (1 + hikePct / 100) ** (1 / 12);

    // Cohort setup — one entry per bucket with its own decay + running cost.
    // `existing` is the reduce of every cohort's remaining base each month.
    let cohortState = null;
    if (attritionMode === "department" && byDepartment.length > 0) {
      cohortState = byDepartment.map((d) => ({
        key: d.department_id, name: d.department_name,
        cost: d.cost,
        factor: 1 - (attritionByDept[d.department_id] ?? attritionByDept[d.department_name] ?? 0) / 100,
      }));
    } else if (attritionMode === "designation" && byDesignation.length > 0) {
      cohortState = byDesignation.map((d) => ({
        key: d.designation, name: d.designation,
        cost: d.cost,
        factor: 1 - (attritionByDesig[d.designation] ?? 0) / 100,
      }));
    }

    // Sum cohort costs — used as the initial `existing` base if in cohort mode.
    const cohortSum = cohortState ? cohortState.reduce((s, c) => s + c.cost, 0) : 0;
    let existing = cohortState ? cohortSum : lastActual.cost;
    let newHires = 0;

    const joinersByMonth = new Map();
    const leaversByMonth = new Map();
    const openByMonth = new Map();
    if (plan && includePlan) {
      for (const m of plan.joiners_by_month || []) joinersByMonth.set(m.month, m);
      for (const m of plan.leavers_by_month || []) leaversByMonth.set(m.month, m);
      for (const m of plan.open_req_hires || []) openByMonth.set(m.month, m);
    }

    const out = timelineData.map((t) => ({
      name: t.name, cost: t.cost, existing: null, newHires: null, total: null,
    }));
    out[out.length - 1] = { ...out[out.length - 1], existing, newHires: 0, total: existing };

    const [yy, mm] = lastActual.name.split("-").map(Number);
    let y = 2000 + yy, m = mm;
    const workspaceFactor = 1 - attritionPct / 100;

    for (let i = 0; i < horizon; i++) {
      m += 1;
      if (m > 12) { m = 1; y += 1; }
      const bucket = `${String(y).slice(2)}-${String(m).padStart(2, "0")}`;

      // 1. Cohort-aware attrition decay.
      if (cohortState) {
        let sum = 0;
        for (const c of cohortState) {
          c.cost = c.cost * c.factor;
          sum += c.cost;
        }
        existing = sum;
      } else {
        existing = existing * workspaceFactor;
      }
      newHires = newHires * (cohortState
        // Use the cohort-weighted-average decay factor so new hires attrit at
        // an aggregate rate rather than the workspace one (best-effort proxy).
        ? (cohortSum > 0
          ? cohortState.reduce((s, c) => s + c.factor * c.cost, 0) / cohortSum
          : 1)
        : workspaceFactor);

      // 2. Compound monthly hike on both segments.
      if (cohortState) {
        for (const c of cohortState) c.cost = c.cost * hikeFactor;
        existing = existing * hikeFactor;
      } else {
        existing = existing * hikeFactor;
      }
      newHires = newHires * hikeFactor;

      // 3. Hiring plan additions.
      let addCost = 0;
      const j = joinersByMonth.get(bucket); if (j) addCost += j.cost_monthly || 0;
      const o = openByMonth.get(bucket);    if (o) addCost += o.cost_monthly || 0;
      newHires += addCost;

      // 4. Leavers drop straight from existing (cohort- or ws-level).
      const l = leaversByMonth.get(bucket);
      if (l) existing = Math.max(0, existing - (l.cost_monthly || 0));

      out.push({
        name: bucket, cost: null,
        existing: Math.round(existing), newHires: Math.round(newHires),
        total: Math.round(existing + newHires),
      });
    }
    return out;
  }, [timelineData, hikePct, horizon, attritionMode, attritionPct, attritionByDept, attritionByDesig, includePlan, plan, lastActual, byDepartment, byDesignation]);

  const totals = useMemo(() => {
    if (!lastActual) return null;
    let sumTotal = 0, sumExisting = 0, sumNew = 0;
    for (const row of merged) {
      if (row.total === null) continue;
      sumTotal += row.total; sumExisting += row.existing || 0; sumNew += row.newHires || 0;
    }
    // Subtract bridge (last-actual duplicated) so we count exactly `horizon`.
    sumTotal -= lastActual.cost;
    sumExisting -= lastActual.cost;
    const endRow = merged[merged.length - 1];
    return {
      currentMonthly: lastActual.cost,
      totalTeam: sumTotal, totalExisting: sumExisting, totalNew: sumNew,
      endMonthly: endRow?.total || lastActual.cost,
    };
  }, [merged, lastActual]);

  // Feb 2026 · Compare-scenario projection — reuse the same month-loop as
  // the primary `merged` but with the compare scenario's saved parameters.
  // Merged into the primary `merged` array so recharts renders both curves
  // on the same axis.
  const compareScenario = scenarios.find((x) => x.id === compareScenarioId);
  const mergedWithCompare = useMemo(() => {
    if (!compareScenario || !lastActual) {
      return merged.map((r) => ({ ...r, compareTotal: null }));
    }
    const s = compareScenario;
    const cHike = (1 + (s.hike_pct || 0) / 100) ** (1 / 12);
    const cWsAttr = 1 - (s.attrition_workspace_pct || 0) / 100;
    // Cohort state for compare scenario
    let cCohorts = null;
    if (s.attrition_mode === "department" && byDepartment.length > 0) {
      cCohorts = byDepartment.map((d) => ({
        key: d.department_id, cost: d.cost,
        factor: 1 - ((s.attrition_by_department || {})[d.department_id] ?? 0) / 100,
      }));
    } else if (s.attrition_mode === "designation" && byDesignation.length > 0) {
      cCohorts = byDesignation.map((d) => ({
        key: d.designation, cost: d.cost,
        factor: 1 - ((s.attrition_by_designation || {})[d.designation] ?? 0) / 100,
      }));
    }
    const cohortSum = cCohorts ? cCohorts.reduce((sum, c) => sum + c.cost, 0) : 0;
    let cExisting = cCohorts ? cohortSum : lastActual.cost;
    let cNew = 0;
    const joinersByMonth = new Map();
    const leaversByMonth = new Map();
    const openByMonth = new Map();
    if (s.include_hiring_plan && plan) {
      for (const mm of plan.joiners_by_month || []) joinersByMonth.set(mm.month, mm);
      for (const mm of plan.leavers_by_month || []) leaversByMonth.set(mm.month, mm);
      for (const mm of plan.open_req_hires || []) openByMonth.set(mm.month, mm);
    }
    // Walk each row and stamp compareTotal for projected rows.
    const out = merged.map((r) => ({ ...r, compareTotal: null }));
    // Find the bridge row (first with total !== null) and start from next.
    let started = false;
    for (let idx = 0; idx < out.length; idx++) {
      const row = out[idx];
      if (row.cost !== null && !started) {
        // historical — leave as is
        if (row.existing !== null) started = true;   // bridge row
        if (started) out[idx].compareTotal = lastActual.cost;
        continue;
      }
      if (!started) continue;
      // Projected row — advance compare simulation one step.
      if (cCohorts) {
        let sumC = 0;
        for (const c of cCohorts) { c.cost = c.cost * c.factor; sumC += c.cost; }
        cExisting = sumC;
      } else {
        cExisting = cExisting * cWsAttr;
      }
      cNew = cNew * (cCohorts ? (cohortSum > 0 ? cCohorts.reduce((s2, c) => s2 + c.factor * c.cost, 0) / cohortSum : 1) : cWsAttr);
      if (cCohorts) {
        for (const c of cCohorts) c.cost = c.cost * cHike;
        cExisting = cExisting * cHike;
      } else {
        cExisting = cExisting * cHike;
      }
      cNew = cNew * cHike;
      const bucket = row.name;
      let addCost = 0;
      const j = joinersByMonth.get(bucket); if (j) addCost += j.cost_monthly || 0;
      const o = openByMonth.get(bucket);    if (o) addCost += o.cost_monthly || 0;
      cNew += addCost;
      const l = leaversByMonth.get(bucket);
      if (l) cExisting = Math.max(0, cExisting - (l.cost_monthly || 0));
      out[idx].compareTotal = Math.round(cExisting + cNew);
    }
    return out;
  }, [merged, compareScenario, plan, byDepartment, byDesignation, lastActual]);

  if (!lastActual) {
    return (
      <Card><CardContent className="p-6 text-xs text-muted-foreground italic text-center">
        Not enough history yet to project.
      </CardContent></Card>
    );
  }

  const planSummary = plan?.summary;
  const selScenario = scenarios.find((x) => x.id === selScenarioId);

  return (
    <Card data-testid="comp-projection-card"><CardContent className="p-4 space-y-4">
      {/* Header + scenario picker */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-semibold">Cost outlook — next {horizon} months</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
              {selScenario ? `Scenario · ${selScenario.name}${dirty ? " · modified" : ""}` : "Ad-hoc projection"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selScenarioId || "__none"} onValueChange={(v) => v === "__none" ? setSelScenarioId("") : loadScenario(v)}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="scenario-picker">
              <SelectValue placeholder="Load scenario…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— Ad-hoc (unsaved) —</SelectItem>
              {scenarios.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {scenarios.length >= 1 && (
            <Select value={compareScenarioId || "__none"} onValueChange={(v) => setCompareScenarioId(v === "__none" ? "" : v)}>
              <SelectTrigger className="h-8 w-52 text-xs" data-testid="scenario-compare-picker">
                <SelectValue placeholder="Overlay scenario…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Compare with… —</SelectItem>
                {scenarios.filter((s) => s.id !== selScenarioId).map((s) => (
                  <SelectItem key={s.id} value={s.id}>vs {s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selScenarioId && dirty && (
            <Button size="sm" variant="outline" onClick={overwriteScenario} data-testid="scenario-save-changes">Save changes</Button>
          )}
          {selScenarioId && (
            <Button size="sm" variant="ghost" onClick={deleteScenario} className="text-red-600 hover:text-red-700" data-testid="scenario-delete">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="sm" onClick={() => setSaveOpen(true)} data-testid="scenario-save-new">
            <Plus className="w-3.5 h-3.5 mr-1" />Save as
          </Button>
        </div>
      </div>

      {/* Controls row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex items-center gap-2 border border-border px-3 py-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">Annual hike</Label>
          <input type="number" min="0" max="30" step="0.5" value={hikePct}
            onChange={(e) => setHikePct(Number(e.target.value) || 0)}
            className="h-8 w-14 text-xs font-mono border border-border px-2 bg-background focus:outline-none focus:border-primary"
            data-testid="projection-hike-pct" />
          <span className="text-xs">%</span>
        </div>
        <label className="flex items-center gap-2 border border-border px-3 py-2 text-xs cursor-pointer">
          <input type="checkbox" checked={includePlan}
            onChange={(e) => setIncludePlan(e.target.checked)}
            data-testid="projection-include-plan" />
          <span>Layer hiring plan</span>
        </label>
        <div className="flex items-center gap-2 border border-border px-3 py-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">Horizon</Label>
          <Select value={String(horizon)} onValueChange={(v) => setHorizon(Number(v))}>
            <SelectTrigger className="h-8 w-full text-xs border-0 shadow-none" data-testid="projection-horizon">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12 mo</SelectItem>
              <SelectItem value="24">24 mo</SelectItem>
              <SelectItem value="36">36 mo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Attrition mode + cohort editor */}
      <div className="border border-border p-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Attrition</div>
            <div className="inline-flex border border-border" data-testid="attrition-mode-toggle">
              {["workspace", "department", "designation"].map((m) => (
                <button key={m} type="button"
                  onClick={() => setAttritionMode(m)}
                  className={`text-[11px] px-2.5 py-1 capitalize ${attritionMode === m ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"} ${m !== "workspace" ? "border-l border-border" : ""}`}
                  data-testid={`attrition-mode-${m}`}
                >{m === "workspace" ? "Workspace" : `By ${m}`}</button>
              ))}
            </div>
          </div>
          {attritionMode !== "workspace" && (
            <div className="text-[10px] text-muted-foreground">
              Weighted avg:
              <span className="ml-1 font-mono text-foreground font-semibold">
                {(() => {
                  const rows = attritionMode === "department" ? byDepartment : byDesignation;
                  const key = attritionMode === "department" ? "department_id" : "designation";
                  const map = attritionMode === "department" ? attritionByDept : attritionByDesig;
                  const totalCost = rows.reduce((s, r) => s + r.cost, 0);
                  if (totalCost <= 0) return "0%";
                  const weighted = rows.reduce((s, r) => s + (r.cost * (map[r[key]] ?? map[r.department_name] ?? 0)), 0) / totalCost;
                  return `${weighted.toFixed(2)}%`;
                })()}
              </span>
            </div>
          )}
        </div>

        {attritionMode === "workspace" ? (
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Monthly attrition:</Label>
            <input type="number" min="0" max="10" step="0.1" value={attritionPct}
              onChange={(e) => setAttritionPct(Number(e.target.value) || 0)}
              className="h-8 w-16 text-xs font-mono border border-border px-2 bg-background focus:outline-none focus:border-primary"
              data-testid="projection-attrition-pct" />
            <span className="text-xs">%</span>
            <span className="text-[10px] text-muted-foreground ml-3">
              Applied uniformly across all cohorts.
            </span>
          </div>
        ) : (
          <CohortAttritionEditor
            rows={attritionMode === "department" ? byDepartment : byDesignation}
            mode={attritionMode}
            valuesByDept={attritionByDept}
            valuesByDesig={attritionByDesig}
            wsId={wsId}
            onChange={(key, val) => {
              if (attritionMode === "department") setAttritionByDept((s) => ({ ...s, [key]: val }));
              else setAttritionByDesig((s) => ({ ...s, [key]: val }));
            }}
          />
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InsightKpi label="Current monthly" value={fmtMoney(totals.currentMonthly)} testid="proj-kpi-current" />
        <InsightKpi label={`Total (${horizon}mo)`} value={fmtMoney(totals.totalTeam)} sub={`${hikePct}% hike · ${attritionMode === "workspace" ? attritionPct + "% attrition" : "cohort attrition"}`} tone="emerald" testid="proj-kpi-total" />
        <InsightKpi label="Existing team" value={fmtMoney(totals.totalExisting)} sub="post-attrition" testid="proj-kpi-existing" />
        <InsightKpi label="New hires" value={fmtMoney(totals.totalNew)} sub={includePlan && planSummary ? `${planSummary.total_joiners} joiners + ${planSummary.total_open_req_hires} open reqs` : includePlan ? "loading…" : "hiring plan off"} tone="emerald" testid="proj-kpi-new" />
      </div>

      {includePlan && planSummary && (planSummary.total_joiners + planSummary.total_leavers + planSummary.total_open_req_hires === 0) && (
        <div className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 px-3 py-2">
          No scheduled joiners, leavers, or open requisitions found in the next {horizon} months. Set <code>date_of_joining</code> / <code>last_working_date</code> on employees or add skill-gap targets on <code>/app/workforce-planning</code> for a richer projection.
        </div>
      )}

      <div style={{ width: "100%", height: 280 }} data-testid="projection-chart">
        <ResponsiveContainer>
          <AreaChart data={mergedWithCompare} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="existingGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF4500" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#FF4500" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="newHiresGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3F51B5" stopOpacity={0.55} />
                <stop offset="95%" stopColor="#3F51B5" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={Math.floor(mergedWithCompare.length / 12) || 0} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v} />
            <RTooltip
              formatter={(v, name) => v === null || v === undefined ? null : [fmtMoney(v), name]}
              labelFormatter={(l) => `20${l}`}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="cost" stroke="#FF4500" strokeWidth={2} fill="url(#existingGrad)" name="Actual" connectNulls={false} />
            <Area type="monotone" dataKey="existing" stackId="proj" stroke="#FF4500" strokeWidth={2} strokeDasharray="5 4" fill="url(#existingGrad)" name={`Projected · existing${compareScenario ? " (primary)" : ""}`} connectNulls />
            <Area type="monotone" dataKey="newHires" stackId="proj" stroke="#3F51B5" strokeWidth={2} strokeDasharray="5 4" fill="url(#newHiresGrad)" name={`Projected · new hires${compareScenario ? " (primary)" : ""}`} connectNulls />
            {compareScenario && (
              <Line type="monotone" dataKey="compareTotal" stroke="#059669" strokeWidth={2.5} strokeDasharray="8 3" dot={false} name={`Overlay · ${compareScenario.name}`} connectNulls />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {compareScenario && (
        <ScenarioDeltaTable rows={mergedWithCompare} primaryName={selScenario?.name || "Ad-hoc"} compareName={compareScenario.name} />
      )}

      <div className="text-[10px] text-muted-foreground italic">
        Model: {attritionMode === "workspace" ? `${attritionPct}%/mo attrition workspace-wide` : `per-${attritionMode} attrition rates`}, {hikePct}% annual hike (monthly-compounded){includePlan ? "; scheduled joiners + leavers + open requisitions layered in per their expected month." : "; hiring plan disabled — enable to layer joiners/leavers/open reqs."}
      </div>

      {saveOpen && (
        <SaveScenarioDialog
          onClose={() => setSaveOpen(false)}
          onSaved={(s) => {
            setScenarios((rows) => [s, ...rows]);
            setSelScenarioId(s.id);
            setTimeout(() => setDirty(false), 50);
            setSaveOpen(false);
          }}
          wsId={wsId}
          payload={currentPayload()}
        />
      )}
    </CardContent></Card>
  );
}

function CohortAttritionEditor({ rows, mode, valuesByDept, valuesByDesig, onChange, wsId }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({}); // key -> {suggested_monthly_pct, exits, exit_details}
  const [drilldownKey, setDrilldownKey] = useState(null);

  const suggest = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/compensation/attrition-suggestions`, { params: { lookback_months: 12 } });
      const list = mode === "department" ? (data.by_department || []) : (data.by_designation || []);
      const keyFor = (r) => mode === "department" ? r.department_id : r.designation;
      const bucket = {};
      let updated = 0;
      for (const s of list) {
        const k = keyFor(s);
        bucket[k] = s;
        onChange(k, s.suggested_monthly_pct || 0);
        updated += 1;
      }
      setSuggestions(bucket);
      toast.success(`Filled ${updated} suggested rate${updated === 1 ? "" : "s"} from last 12 mo`);
    } catch (e) { toast.error(apiErrorMessage(e, "Suggestion failed")); }
    finally { setLoading(false); }
  };
  if (!rows || rows.length === 0) {
    return <div className="text-xs italic text-muted-foreground p-4 text-center">No {mode === "department" ? "departments" : "designations"} with cost yet — add revisions first.</div>;
  }
  const key = mode === "department" ? "department_id" : "designation";
  const labelKey = mode === "department" ? "department_name" : "designation";
  const map = mode === "department" ? valuesByDept : valuesByDesig;

  const drilldown = drilldownKey ? suggestions[drilldownKey] : null;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <Button size="sm" variant="outline" onClick={suggest} disabled={loading} data-testid={`cohort-suggest-${mode}`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Suggest from history
        </Button>
      </div>
      <div className="max-h-56 overflow-y-auto border border-border" data-testid={`cohort-editor-${mode}`}>
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{mode === "department" ? "Department" : "Designation"}</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Cost</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Headcount</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Monthly attrition %</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const k = r[key];
              const val = map[k] ?? 0;
              const sug = suggestions[k];
              const hasDetails = sug && (sug.exit_details || []).length > 0;
              return (
                <tr key={k} className="hover:bg-muted/30">
                  <td className="px-3 py-1.5 font-semibold truncate max-w-[200px]">{r[labelKey]}</td>
                  <td className="text-right px-3 py-1.5 font-mono">{fmtMoney(r.cost)}</td>
                  <td className="text-right px-3 py-1.5 font-mono text-muted-foreground">{r.headcount}</td>
                  <td className="text-right px-3 py-1.5">
                    <input type="number" min="0" max="10" step="0.1" value={val}
                      onChange={(e) => onChange(k, Number(e.target.value) || 0)}
                      className="h-7 w-16 text-xs font-mono border border-border px-2 bg-background focus:outline-none focus:border-primary text-right"
                      data-testid={`cohort-attrition-${k}`} />
                  </td>
                  <td className="text-right px-3 py-1.5">
                    {hasDetails && (
                      <button type="button" onClick={() => setDrilldownKey(k)}
                        className="text-[10px] text-primary hover:underline"
                        data-testid={`cohort-drilldown-${k}`}>
                        Why? →
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {drilldown && (
        <CohortDrilldownDialog
          cohortLabel={rows.find((r) => r[key] === drilldownKey)?.[labelKey] || drilldownKey}
          suggestion={drilldown}
          onClose={() => setDrilldownKey(null)}
        />
      )}
    </div>
  );
}

function CohortDrilldownDialog({ cohortLabel, suggestion, onClose }) {
  const details = suggestion.exit_details || [];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="cohort-drilldown-dialog">
        <DialogHeader>
          <DialogTitle>Why {cohortLabel}?</DialogTitle>
          <DialogDescription>
            Suggested {suggestion.suggested_monthly_pct}%/mo attrition is derived from {details.length} exit signal{details.length === 1 ? "" : "s"} in the last 12 months. Review who left before accepting the rate.
          </DialogDescription>
        </DialogHeader>
        {details.length === 0 ? (
          <div className="text-xs italic text-muted-foreground p-6 text-center">No exit signals captured for this cohort — the suggestion is 0%.</div>
        ) : (
          <div className="border border-border divide-y divide-border max-h-96 overflow-y-auto">
            {details.map((e, i) => (
              <div key={e.employee_id || i} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`drilldown-emp-${e.employee_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.full_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {e.designation}
                    {e.last_working_date ? ` · LWD ${e.last_working_date}` : ""}
                  </div>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 border uppercase tracking-widest ${e.kind === "exit" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {e.kind === "exit" ? (e.status || "exit") : "voided rev"}
                </span>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveScenarioDialog({ onClose, onSaved, wsId, payload }) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/compensation/scenarios`,
        { ...payload, name: name.trim(), notes: notes.trim() || null });
      toast.success(`Scenario "${data.name}" saved`);
      onSaved(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="save-scenario-dialog">
        <DialogHeader>
          <DialogTitle>Save scenario</DialogTitle>
          <DialogDescription>
            Freeze the current hike + attrition + hiring-plan combo under a name so HR and the CFO can reload the exact same projection later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input placeholder="e.g. Moderate hiring · 12% raise · 1% attrition"
              value={name} onChange={(e) => setName(e.target.value)}
              className="h-10" autoFocus data-testid="save-scenario-name" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input placeholder="e.g. Board-approved base case for FY 26"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              className="h-10" data-testid="save-scenario-notes" />
          </div>
          <div className="text-[11px] text-muted-foreground bg-muted/30 p-2 border border-border font-mono">
            <div>Hike: <strong>{payload.hike_pct}%</strong> · Horizon: <strong>{payload.horizon_months}mo</strong> · Attrition: <strong>{payload.attrition_mode}</strong></div>
            <div>Hiring plan: <strong>{payload.include_hiring_plan ? "on" : "off"}</strong></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !name.trim()} data-testid="save-scenario-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save scenario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function SignalsPanel({ alerts, months, onJumpToEmployee }) {
  const [tab, setTab] = useState("raises");
  const raises = alerts.top_raises || [];
  const stagnation = alerts.stagnation || [];
  const outBand = alerts.anomalies?.out_of_band || [];
  const sharp = alerts.anomalies?.sharp_change || [];
  const noRev = alerts.anomalies?.no_revision || [];
  const totalAlerts = outBand.length + sharp.length;
  // Convenience — a signals row jumping the user to the Employees tab for
  // a specific employee, optionally opening the New-revision dialog too.
  const jump = (eid, openDialog = false) => {
    if (!onJumpToEmployee) return;
    onJumpToEmployee(eid, { openDialog });
  };

  const TabPill = ({ v, label, count, tone }) => {
    const active = tab === v;
    const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : tone === "red" ? "text-red-700" : "";
    return (
      <button type="button" onClick={() => setTab(v)}
        className={`text-xs px-3 py-1.5 border-b-2 transition-colors ${active ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        data-testid={`signals-tab-${v}`}>
        {label} {count > 0 && <span className={`ml-1 font-mono ${active ? "" : toneCls}`}>({count})</span>}
      </button>
    );
  };

  return (
    <Card data-testid="comp-signals-panel"><CardContent className="p-0">
      <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-primary" />
        <div className="text-sm font-semibold">HR intelligence signals</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
          Last {months} months · stagnation ≥ {alerts.stagnation_threshold_months} months
        </div>
      </div>
      <div className="flex items-center border-b border-border overflow-x-auto">
        <TabPill v="raises" label="Top raises" count={raises.length} tone="emerald" />
        <TabPill v="stagnation" label="Stagnated" count={stagnation.length} tone="amber" />
        <TabPill v="anomalies" label="Anomalies" count={totalAlerts} tone="red" />
        <TabPill v="norev" label="No revision" count={noRev.length} tone="amber" />
      </div>
      <div className="p-3">
        {tab === "raises" && <RaisesList rows={raises} onOpen={jump} />}
        {tab === "stagnation" && <StagnationList rows={stagnation} onOpen={jump} />}
        {tab === "anomalies" && <AnomaliesList outBand={outBand} sharp={sharp} bands={alerts.band_stats || []} onOpen={jump} />}
        {tab === "norev" && <NoRevisionList rows={noRev} onOpen={jump} />}
      </div>
    </CardContent></Card>
  );
}

function EmptySignal({ msg }) {
  return <div className="text-xs italic text-muted-foreground text-center p-6">{msg}</div>;
}

function RaisesList({ rows, onOpen }) {
  if (rows.length === 0) return <EmptySignal msg="No raises in this window yet — the top-raises leaderboard will populate as new revisions land." />;
  return (
    <div className="overflow-x-auto" data-testid="signals-raises-list">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 uppercase text-[10px] tracking-widest text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-left px-3 py-2">Role · Dept</th>
            <th className="text-right px-3 py-2">Prev cost</th>
            <th className="text-right px-3 py-2">New cost</th>
            <th className="text-right px-3 py-2">Δ %</th>
            <th className="text-right px-3 py-2">Δ ₹</th>
            <th className="text-left px-3 py-2">On</th>
            <th className="text-right px-3 py-2 w-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.employee_id} data-testid={`signal-raise-${r.employee_id}`}
                className="hover:bg-muted/40 cursor-pointer"
                onClick={() => onOpen && onOpen(r.employee_id)}>
              <td className="px-3 py-2 font-semibold">{r.full_name}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.designation} · {r.department_name}</td>
              <td className="text-right px-3 py-2 font-mono">{fmtMoney(r.prev_cost)}</td>
              <td className="text-right px-3 py-2 font-mono">{fmtMoney(r.new_cost)}</td>
              <td className="text-right px-3 py-2 font-mono font-bold text-emerald-700">+{r.delta_pct}%</td>
              <td className="text-right px-3 py-2 font-mono text-emerald-700">+{fmtMoney(r.delta_abs)}</td>
              <td className="px-3 py-2 text-muted-foreground text-[11px]">{r.effective_from}</td>
              <td className="text-right px-3 py-2"><span className="text-[10px] text-primary hover:underline">Open →</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StagnationList({ rows, onOpen }) {
  if (rows.length === 0) return <EmptySignal msg="Nobody's compensation has stagnated past the threshold — great!" />;
  return (
    <div className="overflow-x-auto" data-testid="signals-stagnation-list">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 uppercase text-[10px] tracking-widest text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-left px-3 py-2">Role · Dept</th>
            <th className="text-right px-3 py-2">Current cost</th>
            <th className="text-left px-3 py-2">Since</th>
            <th className="text-right px-3 py-2">Months</th>
            <th className="text-right px-3 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.employee_id} data-testid={`signal-stagnation-${r.employee_id}`} className="hover:bg-muted/40">
              <td className="px-3 py-2 font-semibold cursor-pointer" onClick={() => onOpen && onOpen(r.employee_id)}>{r.full_name}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.designation} · {r.department_name}</td>
              <td className="text-right px-3 py-2 font-mono">{fmtMoney(r.employer_cost_monthly)}</td>
              <td className="px-3 py-2 text-muted-foreground text-[11px]">{r.effective_from}</td>
              <td className={`text-right px-3 py-2 font-mono font-bold ${r.months_since_change >= 24 ? "text-red-700" : "text-amber-700"}`}>
                {r.months_since_change} mo
              </td>
              <td className="text-right px-3 py-2">
                <button type="button" className="text-[10px] px-2 py-0.5 border border-primary text-primary hover:bg-primary/10 rounded-none uppercase tracking-widest"
                  onClick={(e) => { e.stopPropagation(); onOpen && onOpen(r.employee_id, true); }}
                  data-testid={`signal-stagnation-raise-${r.employee_id}`}>Raise</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnomaliesList({ outBand, sharp, bands, onOpen }) {
  if (outBand.length === 0 && sharp.length === 0) {
    return <EmptySignal msg="No compensation anomalies detected — every current comp sits inside its role-band and no revision made a dramatic ±40% jump." />;
  }
  return (
    <div className="space-y-4" data-testid="signals-anomalies-list">
      {sharp.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-2 flex items-center gap-2">
            Sharp change ({sharp.length})
            <span className="normal-case tracking-normal text-muted-foreground font-normal">Latest revision moved employer cost by ≥ 40% — possible typo or genuine outlier.</span>
          </div>
          <div className="border border-red-200 dark:border-red-900/40 divide-y divide-border">
            {sharp.map((r) => (
              <button key={r.employee_id} type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/40 text-left"
                onClick={() => onOpen && onOpen(r.employee_id, true)}
                data-testid={`signal-sharp-${r.employee_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.full_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{r.designation} · {r.department_name} · {r.effective_from} · {r.reason || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold ${r.kind === "increase" ? "text-emerald-700" : "text-red-700"}`}>{r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{fmtMoney(r.prev_cost)} → {fmtMoney(r.new_cost)}</div>
                </div>
                <span className="text-[10px] text-primary hover:underline shrink-0">Fix →</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {outBand.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-2 flex items-center gap-2">
            Out of role band ({outBand.length})
            <span className="normal-case tracking-normal text-muted-foreground font-normal">Employer cost is &gt; 1.5σ from the designation cohort mean.</span>
          </div>
          <div className="border border-amber-200 dark:border-amber-900/40 divide-y divide-border">
            {outBand.map((r) => (
              <button key={r.employee_id} type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/40 text-left"
                onClick={() => onOpen && onOpen(r.employee_id)}
                data-testid={`signal-outband-${r.employee_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.full_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{r.designation} · {r.department_name} · cohort of {r.cohort_size}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold ${r.direction === "above" ? "text-emerald-700" : "text-amber-700"}`}>
                    {r.deviation_pct > 0 ? "+" : ""}{r.deviation_pct}%
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">{fmtMoney(r.employer_cost_monthly)} vs mean {fmtMoney(r.cohort_mean)}</div>
                </div>
                <span className="text-[10px] text-primary hover:underline shrink-0">Open →</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {bands.length > 0 && (
        <details className="text-[11px] text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">Role bands used for anomaly detection ({bands.length})</summary>
          <div className="mt-2 border border-border divide-y divide-border">
            {bands.slice(0, 12).map((b) => (
              <div key={b.designation} className="flex items-center gap-2 px-3 py-1.5" data-testid={`signal-band-${b.designation}`}>
                <div className="flex-1 truncate">{b.designation}</div>
                <div className="font-mono">{b.cohort_size} emp</div>
                <div className="font-mono">μ {fmtMoney(b.mean_cost)}</div>
                <div className="font-mono">band {fmtMoney(b.band_low)} – {fmtMoney(b.band_high)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function NoRevisionList({ rows, onOpen }) {
  if (rows.length === 0) return <EmptySignal msg="Every active employee has a compensation revision — good hygiene!" />;
  return (
    <div className="overflow-x-auto" data-testid="signals-norev-list">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 uppercase text-[10px] tracking-widest text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-left px-3 py-2">Role</th>
            <th className="text-left px-3 py-2">Department</th>
            <th className="text-right px-3 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.slice(0, 50).map((r) => (
            <tr key={r.employee_id} data-testid={`signal-norev-${r.employee_id}`} className="hover:bg-muted/40">
              <td className="px-3 py-2 font-semibold">{r.full_name}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.designation}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.department_name}</td>
              <td className="text-right px-3 py-2">
                <button type="button" className="text-[10px] px-2 py-0.5 border border-primary text-primary hover:bg-primary/10 rounded-none uppercase tracking-widest"
                  onClick={() => onOpen && onOpen(r.employee_id, true)}
                  data-testid={`signal-norev-add-${r.employee_id}`}>Add revision</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && (
        <div className="text-[10px] text-muted-foreground italic p-2 text-center border-t border-border">
          Showing 50 of {rows.length}. Use the Grid tab's "Missing revision only" toggle to see all.
        </div>
      )}
    </div>
  );
}



// Feb 2026 · Scenario delta table — sits below the projection chart when
// a compare-scenario is picked. Row = month, columns = primary total,
// overlay total, delta ₹, delta %.
function ScenarioDeltaTable({ rows, primaryName, compareName }) {
  const projectedRows = rows.filter((r) => r.total !== null);
  if (projectedRows.length < 2) return null;
  // Skip the bridge (first projected row = last actual duplicated).
  const body = projectedRows.slice(1);
  let sumPrimary = 0, sumCompare = 0;
  const enriched = body.map((r) => {
    sumPrimary += r.total || 0;
    sumCompare += r.compareTotal || 0;
    const delta = (r.compareTotal || 0) - (r.total || 0);
    const pct = r.total ? (delta / r.total) * 100 : 0;
    return { ...r, delta, pct };
  });
  const totalDelta = sumCompare - sumPrimary;
  const totalPct = sumPrimary ? (totalDelta / sumPrimary) * 100 : 0;
  return (
    <div className="border border-border" data-testid="scenario-delta-table">
      <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        Month-by-month delta · <span className="normal-case tracking-normal">{compareName} vs {primaryName}</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/20 sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Month</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Primary</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Overlay</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Δ ₹</th>
              <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Δ %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {enriched.map((r) => (
              <tr key={r.name} className="hover:bg-muted/30" data-testid={`scenario-delta-row-${r.name}`}>
                <td className="px-3 py-1.5 font-mono text-muted-foreground">20{r.name}</td>
                <td className="text-right px-3 py-1.5 font-mono">{fmtMoney(r.total || 0)}</td>
                <td className="text-right px-3 py-1.5 font-mono">{fmtMoney(r.compareTotal || 0)}</td>
                <td className={`text-right px-3 py-1.5 font-mono ${r.delta > 0 ? "text-red-700" : r.delta < 0 ? "text-emerald-700" : ""}`}>
                  {r.delta > 0 ? "+" : ""}{fmtMoney(r.delta)}
                </td>
                <td className={`text-right px-3 py-1.5 font-mono font-semibold ${r.pct > 0 ? "text-red-700" : r.pct < 0 ? "text-emerald-700" : ""}`}>
                  {r.pct > 0 ? "+" : ""}{r.pct.toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-primary/5 border-t-2 border-primary/30 sticky bottom-0">
            <tr className="font-semibold">
              <td className="px-3 py-1.5 uppercase text-[10px] tracking-widest text-primary">Total</td>
              <td className="text-right px-3 py-1.5 font-mono text-primary">{fmtMoney(sumPrimary)}</td>
              <td className="text-right px-3 py-1.5 font-mono text-primary">{fmtMoney(sumCompare)}</td>
              <td className={`text-right px-3 py-1.5 font-mono ${totalDelta > 0 ? "text-red-700" : totalDelta < 0 ? "text-emerald-700" : ""}`}>
                {totalDelta > 0 ? "+" : ""}{fmtMoney(totalDelta)}
              </td>
              <td className={`text-right px-3 py-1.5 font-mono ${totalPct > 0 ? "text-red-700" : totalPct < 0 ? "text-emerald-700" : ""}`}>
                {totalPct > 0 ? "+" : ""}{totalPct.toFixed(2)}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

